import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Package, ArrowDownCircle, ArrowUpCircle, Search, Plus, X, Scissors, AlertCircle, Camera, Upload as UploadIcon, ImageIcon, Send, Edit2, PlusCircle, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tallas estandar por defecto (mismas que en Cortes.jsx). Ordenadas asc.
const TALLAS_ADULTO = ['16', '28', '30', '32', '34', '36', '38', '40', '42', '44'];
const TALLAS_NINO = ['2', '4', '6', '8', '10', '12', '14', '16', '28'];

// Mini-grafico SVG inline: dibuja la serie de stocks diarios (ultimos 30 dias).
// La linea va en color ambar, con un area suave debajo que da sensacion de "extracto".
const Sparkline = ({ data = [], width = 100, height = 28, color = '#fbbf24' }) => {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = (max - min) || 1;
    const stepX = data.length > 1 ? width / (data.length - 1) : 0;
    const points = data.map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / range) * (height - 4) - 2;
        return [x, y];
    });
    const linePath = points.map((p, i) => (i === 0 ? `M${p[0].toFixed(1)},${p[1].toFixed(1)}` : `L${p[0].toFixed(1)},${p[1].toFixed(1)}`)).join(' ');
    const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
    const last = points[points.length - 1];
    return (
        <svg width={width} height={height} className="overflow-visible block" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
            <path d={areaPath} fill={color} fillOpacity="0.12" />
            <path d={linePath} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={last[0]} cy={last[1]} r="1.8" fill={color} />
        </svg>
    );
};

const initialForm = {
    tipo: 'entrada',
    sin_corte: false,
    modelo: '',
    talla: '',
    color: '',
    lote_id: '',
    corte_id: '',
    lote_referencia: '',
    cantidad: '',
    paquetes: [],
    // Nueva: lista de {talla, cantidad} para registrar multiples tallas en un bulto
    filasTallas: [],
    // Multiplicador: cada paquete trae todas las tallas listadas, asi que recibir N
    // paquetes multiplica la cantidad de cada talla por N.
    paquetesRecibidos: 1,
    restos: 0,
    defectos: 0,
    faltantes: 0,
    foto_file: null,
    foto_preview: '',
    fecha: new Date().toISOString().split('T')[0],
    observaciones: '',
    // Para salida: producto seleccionado del inventario
    inv_key: '',
    stock_disponible: 0,
    // Para salida: tipo de salida (unidad o paquete) + tamano de paquete + cantidad de paquetes
    salida_modo: 'unidad', // 'unidad' | 'paquete'
    salida_paquetes: '',
    salida_piezas_por_paquete: 0,
    // Buscador de productos en modal de salida
    salida_search: ''
};

const PIEZAS_POR_PAQUETE_OPCIONES = [12, 14, 16];

export const ProductoTerminado = () => {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [movimientos, setMovimientos] = useState([]);
    const [inventario, setInventario] = useState([]);
    const [lotes, setLotes] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [resumenCorte, setResumenCorte] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [searchTerm, setSearchTerm] = useState('');
    const fileInputRef = useRef(null);
    // Edicion
    const [editingMov, setEditingMov] = useState(null); // mov object cuando se edita
    const [editForm, setEditForm] = useState({});
    // Modal foto (zoom + cambiar)
    const [fotoModal, setFotoModal] = useState(null); // mov al abrir foto
    const [fotoNuevoFile, setFotoNuevoFile] = useState(null);
    // Sugerencia de similares al crear entrada
    const [similares, setSimilares] = useState([]);
    const [agregarA, setAgregarA] = useState(null); // mov al que se va a agregar (mismo modal de Nuevo)
    const [submitting, setSubmitting] = useState(false); // Anti doble-click
    // Modal de info completa al hacer click en tarjeta del inventario
    const [infoInv, setInfoInv] = useState(null);
    // Segmento de tallas por defecto cuando se selecciona "Sin corte"
    const [segmentoTallas, setSegmentoTallas] = useState('adulto'); // 'adulto' | 'nino'
    // Cache persistente de cantidades por talla (sobrevive a cambios de segmento Adulto/Nino)
    const tallasCantidadesRef = useRef({});
    // Paginacion del feed de movimientos
    const [feedLimit, setFeedLimit] = useState(20);
    // Paginacion de las tarjetas de inventario agrupadas
    const [invLimit, setInvLimit] = useState(20);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [movRes, invRes, lotesRes, cortesRes] = await Promise.all([
                axios.get(`${API_URL}/api/producto-terminado`),
                axios.get(`${API_URL}/api/producto-terminado/inventario`),
                axios.get(`${API_URL}/api/lotes`),
                axios.get(`${API_URL}/api/producto-terminado/cortes-disponibles`)
            ]);
            setMovimientos(movRes.data);
            setInventario(invRes.data);
            setLotes(lotesRes.data);
            setCortes(cortesRes.data);
        } catch (e) { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    };

    // Auto-llena modelo/color cuando se selecciona corte y carga su resumen
    useEffect(() => {
        if (!form.corte_id || form.sin_corte) { setResumenCorte(null); return; }
        const corte = cortes.find(c => c.id === form.corte_id);
        if (corte) {
            // Construir filas de tallas desde el corte CON sus cantidades (opcion A: numero tal cual)
            const rel = corte.relacion_tela_tallas || [];
            let filasNuevas = [];
            if (Array.isArray(rel) && rel.length > 0) {
                filasNuevas = rel
                    .map(t => ({
                        talla: String(t.talla ?? t.size ?? t.nombre ?? ''),
                        // El numero de cortes se transfiere tal cual como piezas en prod terminado
                        cantidad: (t.cantidad ?? t.piezas ?? t.qty ?? '').toString()
                    }))
                    .filter(f => f.talla);
            } else if (typeof corte.relacion_corte === 'string' && corte.relacion_corte.trim()) {
                filasNuevas = corte.relacion_corte
                    .split(/[-,/\s]+/).map(s => s.trim()).filter(Boolean)
                    .map(t => ({ talla: t, cantidad: '' }));
            }

            setForm(f => ({
                ...f,
                modelo: f.modelo || corte.modelo || '',
                color: f.color || corte.color_tela || '',
                foto_preview: f.foto_preview || corte.foto_url || '',
                observaciones: f.observaciones || [
                    corte.marca ? `Marca: ${corte.marca}` : '',
                    corte.tipo_pantalon ? `Tipo: ${corte.tipo_pantalon}` : '',
                    corte.color_hilo ? `Hilo: ${corte.color_hilo}` : '',
                    corte.relacion_corte ? `Relacion: ${corte.relacion_corte}` : '',
                    corte.piezas_por_paquete ? `Pzs/paq: ${corte.piezas_por_paquete}` : ''
                ].filter(Boolean).join(' · '),
                // Solo pre-cargar si las filas estan vacias o son las del corte ID anterior
                filasTallas: (f.filasTallas && f.filasTallas.length > 0 && f._corte_id_filas === form.corte_id)
                    ? f.filasTallas
                    : filasNuevas,
                _corte_id_filas: form.corte_id
            }));
        }
        axios.get(`${API_URL}/api/producto-terminado/corte/${form.corte_id}/resumen`)
            .then(res => setResumenCorte(res.data))
            .catch(() => setResumenCorte(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.corte_id, form.sin_corte]);

    // Modo "Sin corte vinculado": pre-llenar filasTallas con las tallas estandar
    // del segmento seleccionado (Adulto/Nino), ordenadas asc, con cantidad vacia.
    // Si el usuario cambia de segmento, repuebla con las nuevas tallas.
    // Las cantidades se preservan via tallasCantidadesRef (cache persistente por talla)
    // para que toggle Adulto→Nino→Adulto NO pierda datos.
    useEffect(() => {
        if (!form.sin_corte || form.tipo !== 'entrada' || agregarA) return;
        const tallasDefault = segmentoTallas === 'nino' ? TALLAS_NINO : TALLAS_ADULTO;
        const expected = tallasDefault.join(',');
        const current = (form.filasTallas || []).map(f => f.talla).join(',');
        if (current !== expected) {
            // Antes de repoblar, vuelca las cantidades actuales al cache
            (form.filasTallas || []).forEach(f => {
                if (f.talla) tallasCantidadesRef.current[f.talla] = f.cantidad || '';
            });
            // Construye filas nuevas leyendo del cache
            const filasNuevas = tallasDefault.map(t => ({
                talla: t,
                cantidad: tallasCantidadesRef.current[t] || ''
            }));
            setForm(f => ({ ...f, filasTallas: filasNuevas }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.sin_corte, segmentoTallas, form.tipo, agregarA]);

    // Tallas disponibles del corte seleccionado (para dropdown de Talla)
    const tallasDelCorte = useMemo(() => {
        if (form.sin_corte || !form.corte_id) return [];
        const corte = cortes.find(c => c.id === form.corte_id);
        if (!corte) return [];
        // Soportar distintos formatos: relacion_tela_tallas=[{talla:"32",cantidad:N}], o "32-34-36"
        const rel = corte.relacion_tela_tallas || [];
        if (Array.isArray(rel) && rel.length > 0) {
            return rel.map(t => String(t.talla ?? t.size ?? t.nombre ?? '')).filter(Boolean);
        }
        if (typeof corte.relacion_corte === 'string' && corte.relacion_corte.trim()) {
            return corte.relacion_corte.split(/[-,/\s]+/).map(s => s.trim()).filter(Boolean);
        }
        return [];
    }, [form.corte_id, form.sin_corte, cortes]);

    // Buscar entradas similares cuando cambian modelo+talla+color en una entrada nueva
    useEffect(() => {
        if (!modalOpen || form.tipo !== 'entrada' || agregarA) { setSimilares([]); return; }
        if (!form.modelo || !form.talla || !form.color) { setSimilares([]); return; }
        const t = setTimeout(() => {
            axios.get(`${API_URL}/api/producto-terminado/buscar-similares`, {
                params: { modelo: form.modelo, talla: form.talla, color: form.color }
            }).then(res => setSimilares(res.data || [])).catch(() => setSimilares([]));
        }, 350);
        return () => clearTimeout(t);
    }, [modalOpen, form.modelo, form.talla, form.color, form.tipo, agregarA]);

    const handleFotoChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) { toast.error('Solo imagenes'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => setForm(prev => ({ ...prev, foto_file: f, foto_preview: ev.target.result }));
        reader.readAsDataURL(f);
    };

    const totalPaquetes = useMemo(() => {
        return (form.paquetes || []).reduce((sum, p) => sum + (parseInt(p.piezas_por_paquete) || 0) * (parseInt(p.cantidad_paquetes) || 0), 0);
    }, [form.paquetes]);

    const totalEntrada = useMemo(() => {
        return totalPaquetes + (parseInt(form.restos) || 0);
    }, [totalPaquetes, form.restos]);

    // Total de todas las filas de tallas (para entrada multi-talla) - SIN multiplicador
    const totalFilasTallasBruto = useMemo(() => {
        return (form.filasTallas || []).reduce((s, f) => s + (parseInt(f.cantidad) || 0), 0);
    }, [form.filasTallas]);

    // Total CON multiplicador de paquetes recibidos
    const totalFilasTallas = useMemo(() => {
        const mult = Math.max(1, parseInt(form.paquetesRecibidos) || 1);
        return totalFilasTallasBruto * mult;
    }, [totalFilasTallasBruto, form.paquetesRecibidos]);

    // Helpers para filas de tallas
    const addFilaTalla = () => setForm(f => ({ ...f, filasTallas: [...(f.filasTallas || []), { talla: '', cantidad: '' }] }));
    const removeFilaTalla = (idx) => setForm(f => ({ ...f, filasTallas: f.filasTallas.filter((_, i) => i !== idx) }));
    const updateFilaTalla = (idx, field, value) => setForm(f => {
        const next = f.filasTallas.map((r, i) => i === idx ? { ...r, [field]: value } : r);
        // Sincroniza el cache persistente cuando estamos en modo "Sin corte"
        // para preservar cantidades a traves del toggle Adulto/Nino.
        if (f.sin_corte) {
            const fila = next[idx];
            if (fila && fila.talla) {
                tallasCantidadesRef.current[fila.talla] = fila.cantidad || '';
            }
        }
        return { ...f, filasTallas: next };
    });

    const addPaquete = () => {
        setForm(f => ({ ...f, paquetes: [...(f.paquetes || []), { piezas_por_paquete: 12, cantidad_paquetes: 1 }] }));
    };
    const removePaquete = (idx) => {
        setForm(f => ({ ...f, paquetes: f.paquetes.filter((_, i) => i !== idx) }));
    };
    const updatePaquete = (idx, field, value) => {
        setForm(f => ({
            ...f,
            paquetes: f.paquetes.map((p, i) => i === idx ? { ...p, [field]: value } : p)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return; // Anti doble-click
        if (!form.modelo || !form.color) {
            toast.error('Modelo y color son requeridos'); return;
        }

        // Modo SALIDA: usar inventario seleccionado
        if (form.tipo === 'salida') {
            const modo = form.salida_modo || 'unidad';

            // === SALIDA SURTIDA (Paquete completo o Corrida = mitad) ===
            // En estos modos NO se selecciona talla: el backend descuenta automaticamente
            // de todas las tallas que componen el paquete (vienen surtidos de fabrica).
            if (modo === 'paquete' || modo === 'corrida') {
                if (!form.modelo || !form.color) {
                    toast.error('Selecciona el modelo y color a dar salida'); return;
                }
                const nPaq = parseInt(form.salida_paquetes) || 0;
                if (nPaq <= 0) { toast.error(`Indica al menos 1 ${modo}`); return; }
                try {
                    setSubmitting(true);
                    const r = await axios.post(`${API_URL}/api/producto-terminado`, {
                        tipo: 'salida',
                        modelo: form.modelo,
                        talla: '',
                        color: form.color,
                        lote_id: '', corte_id: '', lote_referencia: '',
                        fecha: form.fecha,
                        observaciones: form.observaciones || '',
                        paquetes: [], restos: 0, defectos: 0, faltantes: 0,
                        cantidad: 0,
                        salida_modo: modo,
                        salida_cantidad_paquetes: nPaq
                    });
                    const total = r?.data?.total_descontado || 0;
                    const tallasN = Object.keys(r?.data?.por_talla || {}).length;
                    toast.success(`Salida ${modo} x${nPaq} (${total} pzas en ${tallasN} talla${tallasN !== 1 ? 's' : ''})`);
                    setModalOpen(false);
                    setForm(initialForm);
                    fetchData();
                } catch (error) { toast.error(error.response?.data?.detail || `Error al registrar salida ${modo}`); }
                finally { setSubmitting(false); }
                return;
            }

            // === SALIDA POR UNIDAD (talla especifica) ===
            if (!form.talla) { toast.error('Selecciona el producto a dar salida'); return; }
            const cantidad = parseInt(form.cantidad) || 0;
            if (cantidad <= 0) { toast.error('Indica una cantidad valida'); return; }
            if (form.stock_disponible && cantidad > form.stock_disponible) {
                toast.error(`Solo hay ${form.stock_disponible} pzas disponibles`); return;
            }
            try {
                setSubmitting(true);
                await axios.post(`${API_URL}/api/producto-terminado`, {
                    tipo: 'salida',
                    modelo: form.modelo,
                    talla: form.talla,
                    color: form.color,
                    lote_id: '', corte_id: '', lote_referencia: '',
                    fecha: form.fecha,
                    observaciones: form.observaciones || '',
                    paquetes: [], restos: 0, defectos: 0, faltantes: 0,
                    cantidad,
                    salida_modo: 'unidad'
                });
                toast.success(`Salida registrada (${cantidad} pzas)`);
                setModalOpen(false);
                setForm(initialForm);
                fetchData();
            } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar'); }
            finally { setSubmitting(false); }
            return;
        }

        // Modo ENTRADA: soporta multi-talla (filasTallas) o single-talla (modo legacy con form.talla)
        const filasValidas = (form.filasTallas || []).filter(r => (r.talla || '').trim() !== '' && (parseInt(r.cantidad) || 0) > 0);
        const esMultiTalla = filasValidas.length > 0;

        if (!esMultiTalla && (!form.talla || totalEntrada <= 0)) {
            toast.error('Agrega al menos una fila de talla con piezas'); return;
        }

        try {
            setSubmitting(true);

            // AGREGAR a registro existente: NUEVO FLUJO multi-talla con composicion congelada.
            // El form.filasTallas viene prellenado con la composicion del paquete original (read-only).
            // El usuario pone N paquetes nuevos, y nosotros creamos N nuevos movs (uno por talla)
            // multiplicando cantidad/paquete por N.
            if (agregarA) {
                const mult = Math.max(1, parseInt(form.paquetesRecibidos) || 1);
                const filasOK = (form.filasTallas || []).filter(r => (r.talla || '').trim() !== '' && (parseInt(r.cantidad) || 0) > 0);
                if (filasOK.length === 0) {
                    toast.error('No hay tallas en la composicion del registro original');
                    return;
                }
                const transId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));
                const ordenadas = [...filasOK].map(f => ({ talla: f.talla, cantidad: (parseInt(f.cantidad) || 0) * mult }));
                const ordSorted = [...ordenadas].sort((a, b) => b.cantidad - a.cantidad);
                const globalRestos = parseInt(form.restos) || 0;
                const globalDefectos = parseInt(form.defectos) || 0;
                const globalFaltantes = parseInt(form.faltantes) || 0;
                let creados = 0;
                for (let i = 0; i < ordSorted.length; i++) {
                    const fila = ordSorted[i];
                    const restosApl = i === 0 ? globalRestos : 0;
                    const defApl = i === 0 ? globalDefectos : 0;
                    const faltApl = i === 0 ? globalFaltantes : 0;
                    const payload = {
                        tipo: 'entrada',
                        modelo: form.modelo,
                        talla: fila.talla,
                        color: form.color,
                        lote_id: form.lote_id || '',
                        corte_id: form.sin_corte ? '' : (form.corte_id || ''),
                        lote_referencia: form.sin_corte ? (form.lote_referencia || '') : '',
                        fecha: form.fecha,
                        observaciones: form.observaciones || '',
                        paquetes: [],
                        restos: restosApl,
                        defectos: defApl,
                        faltantes: faltApl,
                        cantidad: fila.cantidad + restosApl,
                        transaccion_id: transId,
                        paquetes_recibidos: mult
                    };
                    await axios.post(`${API_URL}/api/producto-terminado`, payload);
                    creados++;
                }
                const totalSumado = ordSorted.reduce((s, f) => s + f.cantidad, 0) + globalRestos;
                toast.success(`Sumados ${mult} paquete${mult > 1 ? 's' : ''} (${totalSumado} pzas) al modelo ${form.modelo} ${form.color}`);
                setModalOpen(false);
                setForm(initialForm);
                setResumenCorte(null);
                setAgregarA(null);
                fetchData();
                return;
            }

            // Subir foto UNA vez al corte (si aplica) para que las nuevas entradas la hereden
            let fotoYaSubida = false;
            if (form.foto_file && !form.sin_corte && form.corte_id) {
                const fd = new FormData();
                fd.append('file', form.foto_file);
                try {
                    await axios.post(`${API_URL}/api/cortes/${form.corte_id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    fotoYaSubida = true;
                } catch (err) { /* no crash */ }
            }

            if (esMultiTalla) {
                // Un POST por talla pero compartiendo transaccion_id para agruparlos en la lista
                const transId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));
                const mult = Math.max(1, parseInt(form.paquetesRecibidos) || 1);
                // Multiplicar cantidad de cada talla por la cantidad de paquetes recibidos
                const filasMultiplicadas = filasValidas.map(f => ({
                    talla: f.talla,
                    cantidad: (parseInt(f.cantidad) || 0) * mult
                }));
                const ordenadas = [...filasMultiplicadas].sort((a, b) => b.cantidad - a.cantidad);
                const globalRestos = parseInt(form.restos) || 0;
                const globalDefectos = parseInt(form.defectos) || 0;
                const globalFaltantes = parseInt(form.faltantes) || 0;
                let creados = 0, primerMovId = null;
                for (let i = 0; i < ordenadas.length; i++) {
                    const fila = ordenadas[i];
                    const cantTalla = parseInt(fila.cantidad) || 0;
                    const restosApl = i === 0 ? globalRestos : 0;
                    const defApl = i === 0 ? globalDefectos : 0;
                    const faltApl = i === 0 ? globalFaltantes : 0;
                    const payload = {
                        tipo: 'entrada',
                        modelo: form.modelo,
                        talla: fila.talla,
                        color: form.color,
                        lote_id: form.lote_id || '',
                        corte_id: form.sin_corte ? '' : (form.corte_id || ''),
                        lote_referencia: form.sin_corte ? (form.lote_referencia || '') : '',
                        fecha: form.fecha,
                        observaciones: form.observaciones || '',
                        paquetes: [],
                        restos: restosApl,
                        defectos: defApl,
                        faltantes: faltApl,
                        cantidad: cantTalla + restosApl,
                        transaccion_id: transId,
                        paquetes_recibidos: mult
                    };
                    const r = await axios.post(`${API_URL}/api/producto-terminado`, payload);
                    if (i === 0) primerMovId = r?.data?.id;
                    creados++;
                }
                if (form.foto_file && !fotoYaSubida && primerMovId) {
                    const fd = new FormData();
                    fd.append('file', form.foto_file);
                    try {
                        await axios.post(`${API_URL}/api/producto-terminado/${primerMovId}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    } catch (err) { /* no crash */ }
                }
                toast.success(`${creados} entradas registradas (${totalFilasTallas + globalRestos} pzas, ${form.paquetesRecibidos || 1} paquete${(form.paquetesRecibidos || 1) > 1 ? 's' : ''})`);
            } else {
                // Modo single-talla (paquetes legacy)
                const payload = {
                    tipo: 'entrada',
                    modelo: form.modelo,
                    talla: form.talla,
                    color: form.color,
                    lote_id: form.lote_id || '',
                    corte_id: form.sin_corte ? '' : (form.corte_id || ''),
                    lote_referencia: form.sin_corte ? (form.lote_referencia || '') : '',
                    fecha: form.fecha,
                    observaciones: form.observaciones || '',
                    paquetes: (form.paquetes || [])
                        .filter(p => parseInt(p.piezas_por_paquete) > 0 && parseInt(p.cantidad_paquetes) > 0)
                        .map(p => ({ piezas_por_paquete: parseInt(p.piezas_por_paquete), cantidad_paquetes: parseInt(p.cantidad_paquetes) })),
                    restos: parseInt(form.restos) || 0,
                    defectos: parseInt(form.defectos) || 0,
                    faltantes: parseInt(form.faltantes) || 0,
                    cantidad: totalEntrada
                };
                const res = await axios.post(`${API_URL}/api/producto-terminado`, payload);
                const movId = res?.data?.id;
                if (form.foto_file && !fotoYaSubida && movId) {
                    const fd = new FormData();
                    fd.append('file', form.foto_file);
                    try {
                        await axios.post(`${API_URL}/api/producto-terminado/${movId}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    } catch (err) { /* no crash */ }
                }
                toast.success(`Entrada registrada (${totalEntrada} pzas)`);
            }

            setModalOpen(false);
            setForm(initialForm);
            setResumenCorte(null);
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar'); }
        finally { setSubmitting(false); }
    };

    const handleDeleteMov = async (mov) => {
        const detalle = `${mov.tipo === 'entrada' ? 'entrada' : 'salida'} de ${mov.cantidad} pzas (${mov.modelo} ${mov.talla} ${mov.color}) del ${mov.fecha}`;
        if (!window.confirm(`¿Eliminar esta ${detalle}?\n\nEsta accion no se puede deshacer.`)) return;
        try {
            await axios.delete(`${API_URL}/api/producto-terminado/${mov.id}`);
            toast.success('Movimiento eliminado');
            fetchData();
        } catch (e) {
            const detail = e.response?.data?.detail || '';
            // Si hay salidas/ventas asociadas, ofrecer cascada
            if (detail.includes('No se puede eliminar') || detail.includes('vendieron')) {
                if (window.confirm(`${detail}\n\n¿Deseas FORZAR la eliminacion y borrar TAMBIEN las ventas/salidas asociadas?`)) {
                    try {
                        await axios.delete(`${API_URL}/api/producto-terminado/${mov.id}?force=true`);
                        toast.success('Movimiento y ventas asociadas eliminados');
                        fetchData();
                    } catch (err) {
                        toast.error(err.response?.data?.detail || 'Error al forzar eliminacion');
                    }
                }
            } else {
                toast.error(detail || 'Error al eliminar');
            }
        }
    };

    const handleResetAll = async () => {
        const promptText = window.prompt(
            '⚠️ Esto BORRARA TODO el inventario de Producto Terminado.\n\n' +
            'Para confirmar, escribe exactamente:\n\nBORRAR TODO'
        );
        if (promptText !== 'BORRAR TODO') {
            if (promptText !== null) toast.error('Confirmacion invalida. Operacion cancelada.');
            return;
        }
        if (!window.confirm('Ultima confirmacion: ¿estas SEGURO de borrar todo el inventario? Esta accion es irreversible.')) return;
        try {
            const r = await axios.post(`${API_URL}/api/producto-terminado/reset`, { confirm: 'BORRAR TODO' });
            toast.success(`Inventario reseteado. ${r.data.deleted} movimientos eliminados.`);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al resetear');
        }
    };

    const handleDeleteGroup = async (g) => {
        const m = g.primero;
        const detalle = `${m.tipo === 'entrada' ? 'entrada' : 'salida'} multi-talla (${g.movs.length} tallas, total ${g.total} pzas) del ${m.fecha}`;
        if (!window.confirm(`¿Eliminar esta ${detalle}?\n\nSe eliminaran los ${g.movs.length} registros del grupo.\nEsta accion no se puede deshacer.`)) return;
        let force = false;
        for (let i = 0; i < g.movs.length; i++) {
            const mv = g.movs[i];
            try {
                await axios.delete(`${API_URL}/api/producto-terminado/${mv.id}${force ? '?force=true' : ''}`);
            } catch (e) {
                const detail = e.response?.data?.detail || '';
                if (!force && (detail.includes('No se puede eliminar') || detail.includes('vendieron'))) {
                    if (window.confirm(`${detail}\n\n¿Forzar eliminacion en cascada (borra tambien ventas/salidas asociadas) para TODOS los registros restantes del grupo?`)) {
                        force = true;
                        try {
                            await axios.delete(`${API_URL}/api/producto-terminado/${mv.id}?force=true`);
                        } catch (err) {
                            toast.error(err.response?.data?.detail || 'Error al forzar');
                            fetchData();
                            return;
                        }
                    } else {
                        toast.error('Operacion cancelada parcialmente');
                        fetchData();
                        return;
                    }
                } else {
                    toast.error(detail || 'Error al eliminar el grupo');
                    fetchData();
                    return;
                }
            }
        }
        toast.success(`Grupo eliminado (${g.movs.length} registros)`);
        fetchData();
    };

    const publicarEnTienda = (mov) => {
        const params = new URLSearchParams({
            modelo: mov.modelo || '',
            color: mov.color || '',
            foto_url: mov.foto_url || ''
        });
        navigate(`/productos?${params.toString()}`);
    };

    const openModal = (tipo) => {
        // Limpia el cache de cantidades por talla al abrir un modal nuevo
        tallasCantidadesRef.current = {};
        setForm({ ...initialForm, tipo });
        setResumenCorte(null); setAgregarA(null); setSimilares([]); setModalOpen(true);
    };

    const openAgregarModal = (mov) => {
        // Pre-rellena el form con los datos del mov para "agregar a existente".
        // Nuevo flujo: multi-talla con composicion congelada del registro original.
        // El usuario solo puede editar: cantidad de paquetes nuevos, restos, defectos, faltantes.
        const k = `${mov.modelo || ''}|${mov.color || ''}`;
        const paqInfo = paquetesByModelo[k];
        let filasComposicion = [];
        if (paqInfo && paqInfo.composicion && Object.keys(paqInfo.composicion).length > 0) {
            // Usar composicion ya calculada (varias tallas mixtas en un paquete)
            filasComposicion = Object.entries(paqInfo.composicion)
                .map(([talla, cantPorPaq]) => ({ talla, cantidad: String(cantPorPaq) }));
        } else {
            // Fallback: 1 talla con 1 pieza/paq
            filasComposicion = [{ talla: mov.talla || '', cantidad: '1' }];
        }
        // Ordenar tallas asc
        filasComposicion.sort((a, b) => {
            const na = parseFloat(a.talla); const nb = parseFloat(b.talla);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            if (!isNaN(na)) return -1;
            if (!isNaN(nb)) return 1;
            return (a.talla || '').localeCompare(b.talla || '');
        });
        setAgregarA({ ...mov, _composicionLocked: true });
        setForm({
            ...initialForm,
            tipo: 'entrada',
            sin_corte: !mov.corte_id,
            modelo: mov.modelo || '',
            color: mov.color || '',
            corte_id: mov.corte_id || '',
            lote_id: mov.lote_id || '',
            lote_referencia: mov.lote_referencia || '',
            paquetes: [],
            filasTallas: filasComposicion,
            paquetesRecibidos: 1,
            restos: 0,
            defectos: 0,
            faltantes: 0
        });
        setResumenCorte(null);
        setSimilares([]);
        setModalOpen(true);
    };

    const openEditModal = (mov) => {
        setEditingMov(mov);
        setEditForm({
            modelo: mov.modelo || '',
            talla: mov.talla || '',
            color: mov.color || '',
            fecha: mov.fecha || '',
            paquetes: (mov.paquetes || []).map(p => ({ ...p })),
            restos: mov.restos || 0,
            defectos: mov.defectos || 0,
            faltantes: mov.faltantes || 0,
            cantidad: mov.cantidad || 0,
            observaciones: mov.observaciones || ''
        });
    };

    const handleEditSubmit = async () => {
        if (!editingMov) return;
        try {
            const payload = {
                modelo: editForm.modelo,
                talla: editForm.talla,
                color: editForm.color,
                fecha: editForm.fecha,
                paquetes: (editForm.paquetes || [])
                    .filter(p => parseInt(p.piezas_por_paquete) > 0 && parseInt(p.cantidad_paquetes) > 0)
                    .map(p => ({ piezas_por_paquete: parseInt(p.piezas_por_paquete), cantidad_paquetes: parseInt(p.cantidad_paquetes) })),
                restos: parseInt(editForm.restos) || 0,
                defectos: parseInt(editForm.defectos) || 0,
                faltantes: parseInt(editForm.faltantes) || 0,
                observaciones: editForm.observaciones || ''
            };
            if (editingMov.tipo === 'salida') {
                payload.cantidad = parseInt(editForm.cantidad) || 0;
            }
            await axios.put(`${API_URL}/api/producto-terminado/${editingMov.id}`, payload);
            toast.success('Movimiento actualizado');
            setEditingMov(null);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar');
        }
    };

    const handleFotoUpload = async () => {
        if (!fotoModal || !fotoNuevoFile) return;
        try {
            const fd = new FormData();
            fd.append('file', fotoNuevoFile);
            await axios.post(`${API_URL}/api/producto-terminado/${fotoModal.id}/foto`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Foto actualizada');
            setFotoModal(null);
            setFotoNuevoFile(null);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al subir foto');
        }
    };

    const updateEditPaquete = (idx, field, value) => {
        setEditForm(f => ({
            ...f,
            paquetes: f.paquetes.map((p, i) => i === idx ? { ...p, [field]: value } : p)
        }));
    };

    const getLoteNumero = (id) => lotes.find(l => l.id === id)?.numero_lote || '-';
    const getCorteLabel = (id) => {
        const c = cortes.find(c => c.id === id);
        return c ? `${c.modelo} - ${c.color_tela}` : '-';
    };

    const filtered = movimientos.filter(m =>
        (m.modelo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.color || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.talla || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const inventarioFiltrado = inventario.filter(i =>
        i.stock > 0 && (
            !searchTerm ||
            (i.modelo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (i.color || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (i.talla || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    // Agrupa el inventario por (modelo, color) en una sola tarjeta.
    // Dentro lista todas las tallas con su stock individual y suma total.
    // Orden de tallas naturales: numericas (28, 30, 32...) primero ascendente, luego alfabeticas (S, M, L, XL).
    const inventarioAgrupado = useMemo(() => {
        const sortTallas = (a, b) => {
            const na = parseFloat(a.talla);
            const nb = parseFloat(b.talla);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            if (!isNaN(na)) return -1;
            if (!isNaN(nb)) return 1;
            return (a.talla || '').localeCompare(b.talla || '');
        };
        const map = {};
        for (const it of inventarioFiltrado) {
            const k = `${it.modelo || ''}|${it.color || ''}`;
            if (!map[k]) {
                map[k] = {
                    modelo: it.modelo || '',
                    color: it.color || '',
                    foto_url: it.foto_url || '',
                    tallas: [],
                    stockTotal: 0
                };
            }
            map[k].tallas.push({ talla: it.talla, stock: it.stock, foto_url: it.foto_url || '' });
            map[k].stockTotal += (it.stock || 0);
            if (!map[k].foto_url && it.foto_url) map[k].foto_url = it.foto_url;
        }
        return Object.values(map).map(g => ({ ...g, tallas: g.tallas.sort(sortTallas) }));
    }, [inventarioFiltrado]);

    // Agrupa movimientos con el mismo transaccion_id en una sola fila visual.
    // Cada elemento del array es { isGroup, movs: [...], total, tallas: 'A/B/C', primero }
    const groupedMovs = useMemo(() => {
        const seen = new Set();
        const result = [];
        for (const m of filtered) {
            if (m.transaccion_id && seen.has(m.transaccion_id)) continue;
            if (m.transaccion_id) {
                seen.add(m.transaccion_id);
                const groupMovs = filtered.filter(x => x.transaccion_id === m.transaccion_id);
                if (groupMovs.length > 1) {
                    const total = groupMovs.reduce((s, g) => s + (g.cantidad || 0), 0);
                    result.push({
                        isGroup: true,
                        id: m.transaccion_id,
                        movs: groupMovs,
                        total,
                        tallasText: groupMovs.map(g => `${g.talla}:${g.cantidad}`).join(' · '),
                        primero: groupMovs[0]
                    });
                    continue;
                }
            }
            result.push({ isGroup: false, id: m.id, movs: [m], primero: m });
        }
        return result;
    }, [filtered]);

    // Paquetes registrados por (modelo, color):
    // - total_paquetes = suma de paquetesRecibidos en todas las entradas (o 1 por entrada si no se registro paquetesRecibidos)
    // - composicion = mapa { talla: piezas/paquete } basado en como se distribuye la cantidad / paquetesRecibidos
    // - piezas_por_paquete_total = suma de la composicion
    // Se asume "paquetes uniformes" (todas las entradas del mismo modelo+color tienen la misma composicion).
    // Si hay multiples transacciones, agrupamos por transaccion_id y tomamos la primera para la composicion.
    const paquetesByModelo = useMemo(() => {
        const map = {};
        const seenTx = new Set();
        // Agrupar entradas por transaccion_id
        const entradasOrdenadas = movimientos
            .filter(m => m.tipo === 'entrada')
            .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        const txToMovs = {};
        for (const m of entradasOrdenadas) {
            const tx = m.transaccion_id || `single-${m.id}`;
            if (!txToMovs[tx]) txToMovs[tx] = [];
            txToMovs[tx].push(m);
        }
        for (const tx of Object.keys(txToMovs)) {
            if (seenTx.has(tx)) continue;
            seenTx.add(tx);
            const movs = txToMovs[tx];
            const m0 = movs[0];
            const k = `${m0.modelo || ''}|${m0.color || ''}`;
            // Cantidad de paquetes registrados en esta transaccion (uno solo, todos los movs del tx comparten)
            const paqRecibidos = parseInt(m0.paquetes_recibidos || m0.paquetesRecibidos || 1) || 1;
            // Composicion del paquete: cada talla aporta cantidad/paqRecibidos por paquete
            const composicion = {};
            let piezasPorPaquete = 0;
            for (const mv of movs) {
                const porPaq = Math.round((mv.cantidad || 0) / paqRecibidos);
                if (porPaq > 0) {
                    composicion[mv.talla] = (composicion[mv.talla] || 0) + porPaq;
                    piezasPorPaquete += porPaq;
                }
            }
            if (!map[k]) {
                map[k] = {
                    total_paquetes: 0,
                    composicion: null, // primera composicion vista
                    piezas_por_paquete: 0,
                    transacciones: []
                };
            }
            map[k].total_paquetes += paqRecibidos;
            map[k].transacciones.push({ tx, paquetes: paqRecibidos, composicion, fecha: m0.fecha });
            if (!map[k].composicion && piezasPorPaquete > 0) {
                map[k].composicion = composicion;
                map[k].piezas_por_paquete = piezasPorPaquete;
            }
        }
        return map;
    }, [movimientos]);

    // Saldo corriente por (modelo, talla, color): recorre TODOS los movimientos en orden
    // ascendente y guarda stock_antes/stock_despues para cada movimiento.
    const saldoMap = useMemo(() => {
        const stockKey = (mv) => `${mv.modelo || ''}|${mv.talla || ''}|${mv.color || ''}`;
        const sorted = [...movimientos].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        const stocks = {};
        const map = {};
        for (const mv of sorted) {
            const k = stockKey(mv);
            const antes = stocks[k] || 0;
            const delta = (mv.tipo === 'entrada' ? 1 : -1) * (mv.cantidad || 0);
            const despues = antes + delta;
            stocks[k] = despues;
            map[mv.id] = { antes, despues };
        }
        return map;
    }, [movimientos]);

    // Sparkline: para cada (modelo, talla, color) calcula los stocks diarios de los
    // ultimos 30 dias (uno por dia, end-of-day). Se usa un array de 30 numeros para
    // dibujar un mini-grafico SVG en cada tarjeta de inventario.
    const sparklineByKey = useMemo(() => {
        const stockKey = (mv) => `${mv.modelo || ''}|${mv.talla || ''}|${mv.color || ''}`;
        const sorted = [...movimientos].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        // Acumular movs por clave + por dia
        const movsByKeyDay = {};
        for (const mv of sorted) {
            const k = stockKey(mv);
            const day = (mv.created_at || mv.fecha || '').slice(0, 10);
            if (!day) continue;
            const delta = (mv.tipo === 'entrada' ? 1 : -1) * (mv.cantidad || 0);
            if (!movsByKeyDay[k]) movsByKeyDay[k] = {};
            movsByKeyDay[k][day] = (movsByKeyDay[k][day] || 0) + delta;
        }
        // Generar arr de 30 dias por clave
        const result = {};
        const today = new Date();
        const days = [];
        for (let i = 29; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            days.push(d.toISOString().slice(0, 10));
        }
        for (const k of Object.keys(movsByKeyDay)) {
            const deltasByDay = movsByKeyDay[k];
            // Stock acumulado ANTES del primer dia de la ventana
            const firstDay = days[0];
            let cumul = 0;
            for (const day of Object.keys(deltasByDay)) {
                if (day < firstDay) cumul += deltasByDay[day];
            }
            const series = [];
            for (const day of days) {
                cumul += (deltasByDay[day] || 0);
                series.push(cumul);
            }
            result[k] = series;
        }
        return result;
    }, [movimientos]);

    const handleBackfillTransaccionId = async () => {
        if (!window.confirm('Esto agrupara los registros viejos (sin transaccion_id) que claramente fueron parte de una misma operacion multi-talla.\n\n¿Continuar?')) return;
        try {
            const r = await axios.post(`${API_URL}/api/producto-terminado/backfill-transaccion-id`);
            toast.success(`${r.data.registros_agrupados} registros agrupados en ${r.data.grupos_creados} transacciones`);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al agrupar registros');
        }
    };


    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="producto-terminado-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Producto Terminado</h1>
                    <p className="text-zinc-400 mt-1">Control de pantalones terminados por paquetes y por corte</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => openModal('entrada')} className="bg-emerald-500 hover:bg-emerald-600" data-testid="btn-entrada-pt">
                        <ArrowDownCircle className="w-5 h-5 mr-2" /> Entrada
                    </Button>
                    <Button onClick={() => openModal('salida')} className="bg-red-500 hover:bg-red-600" data-testid="btn-salida-pt">
                        <ArrowUpCircle className="w-5 h-5 mr-2" /> Salida
                    </Button>
                    {isAdmin && (
                        <Button onClick={handleResetAll} variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10" data-testid="btn-reset-pt" title="Borrar TODO el inventario para empezar de nuevo">
                            <Trash2 className="w-4 h-4 mr-2" /> Resetear inventario
                        </Button>
                    )}
                </div>
            </div>

            {/* Cortes pendientes */}
            {cortes.filter(c => c.pendiente > 0).length > 0 && (
                <Card className="card border-amber-400/40 bg-amber-400/5">
                    <CardContent className="p-4">
                        <p className="font-semibold text-amber-400 flex items-center gap-2 mb-3">
                            <Scissors className="w-4 h-4" /> Cortes con piezas pendientes por entregar
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {cortes.filter(c => c.pendiente > 0).slice(0, 6).map(c => (
                                <div key={c.id} className="bg-zinc-800/60 rounded p-2 text-sm">
                                    <p className="font-medium text-zinc-100">{c.modelo} <span className="text-zinc-400">- {c.color_tela}</span></p>
                                    <p className="text-xs text-zinc-400">
                                        Recibido: <span className="text-emerald-400">{c.recibido}</span> / Total: {c.piezas_cortadas} ·
                                        <span className="text-amber-400 ml-1">Pendiente: {c.pendiente}</span>
                                    </p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Barra de busqueda (filtra tarjetas de inventario y tabla de movimientos) */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Buscar por modelo, talla o color..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setFeedLimit(20); setInvLimit(20); }} className="pl-9" data-testid="search-pt" />
            </div>

            {/* Inventory Cards - Agrupadas por (modelo, color), tallas listadas dentro */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {inventarioAgrupado.slice(0, invLimit).map((g, idx) => {
                    // Sumar series sparkline de todas las tallas del grupo (stock combinado a lo largo del tiempo)
                    const seriesCombinada = (() => {
                        const len = 30;
                        const out = new Array(len).fill(0);
                        for (const t of g.tallas) {
                            const k = `${g.modelo}|${t.talla}|${g.color}`;
                            const s = sparklineByKey[k];
                            if (s && s.length === len) {
                                for (let i = 0; i < len; i++) out[i] += s[i];
                            }
                        }
                        return out;
                    })();
                    const tendenciaUp = seriesCombinada.length >= 2 && seriesCombinada[seriesCombinada.length - 1] > seriesCombinada[0];
                    const tendenciaDown = seriesCombinada.length >= 2 && seriesCombinada[seriesCombinada.length - 1] < seriesCombinada[0];
                    const sparkColor = tendenciaUp ? '#10b981' : tendenciaDown ? '#ef4444' : '#fbbf24';
                    return (
                        <Card
                            key={`${g.modelo}|${g.color}|${idx}`}
                            className="card cursor-pointer hover:ring-1 hover:ring-amber-400 transition"
                            onClick={() => setInfoInv({ modelo: g.modelo, color: g.color, talla: null, stock: g.stockTotal, foto_url: g.foto_url })}
                            data-testid={`inv-card-${idx}`}
                        >
                            <CardContent className="p-3">
                                {/* Header: foto + modelo · color + total */}
                                <div className="flex items-center gap-3">
                                    {g.foto_url ? (
                                        <img
                                            src={`${API_URL}${g.foto_url}`}
                                            alt={g.modelo}
                                            className="w-14 h-14 rounded-lg object-cover border border-zinc-700 shrink-0"
                                        />
                                    ) : (
                                        <div className="w-14 h-14 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0">
                                            <ImageIcon className="w-6 h-6 text-zinc-600" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-zinc-200 truncate" title={g.modelo}>{g.modelo}</p>
                                        <p className="text-xs text-zinc-500 truncate">{g.color}</p>
                                        <p className="text-lg font-bold text-amber-400 mt-0.5">
                                            {g.stockTotal} pzas
                                            <span className="text-[10px] text-zinc-500 font-normal ml-2">{g.tallas.length} tallas</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Desglose por talla (chips clickeables) */}
                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 mt-3 pt-3 border-t border-zinc-800">
                                    {g.tallas.map(t => (
                                        <button
                                            key={t.talla}
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setInfoInv({ modelo: g.modelo, color: g.color, talla: t.talla, stock: t.stock, foto_url: t.foto_url || g.foto_url });
                                            }}
                                            className="bg-zinc-800/60 rounded px-1.5 py-1 text-center border border-zinc-800 hover:border-amber-400 hover:bg-zinc-800 transition"
                                            data-testid={`inv-talla-${idx}-${t.talla}`}
                                            title={`Ver movimientos de talla ${t.talla}`}
                                        >
                                            <p className="text-[10px] text-zinc-500 leading-none">T-{t.talla}</p>
                                            <p className="text-xs font-semibold text-zinc-200 mt-0.5">{t.stock}</p>
                                        </button>
                                    ))}
                                </div>

                                {/* Sparkline 30d */}
                                {seriesCombinada.some(v => v !== 0) && (
                                    <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center justify-between gap-2" data-testid={`spark-${idx}`}>
                                        <span className="text-[9px] text-zinc-500 uppercase tracking-wider shrink-0">30d</span>
                                        <Sparkline data={seriesCombinada} color={sparkColor} width={140} height={24} />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
                {inventario.filter(i => i.stock > 0).length === 0 && (
                    <Card className="card col-span-full"><CardContent className="p-8 text-center text-zinc-400">Sin inventario</CardContent></Card>
                )}
                {inventarioAgrupado.length > invLimit && (
                    <div className="col-span-full flex justify-center pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setInvLimit(l => l + 20)}
                            className="text-xs"
                            data-testid="btn-ver-mas-inv"
                        >
                            Ver mas ({inventarioAgrupado.length - invLimit} restantes)
                        </Button>
                    </div>
                )}
            </div>

            {/* Movements Feed - Estilo historial bancario con saldo corriente */}
            <div className="space-y-3" data-testid="pt-historial">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">Historial de movimientos</h2>
                    {isAdmin && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleBackfillTransaccionId}
                            className="text-[11px] text-amber-400 hover:text-amber-300"
                            title="Agrupa registros viejos sin transaccion_id"
                            data-testid="btn-backfill-transid"
                        >
                            Agrupar registros viejos
                        </Button>
                    )}
                </div>
                {groupedMovs.length > 0 ? groupedMovs.slice(0, feedLimit).map(g => {
                    const m = g.primero;
                    const isEntrada = m.tipo === 'entrada';
                    const totalCantidad = g.isGroup ? g.total : m.cantidad;
                    const totalRestos = g.isGroup ? g.movs.reduce((s, x) => s + (x.restos || 0), 0) : (m.restos || 0);
                    const totalDefectos = g.isGroup ? g.movs.reduce((s, x) => s + (x.defectos || 0), 0) : (m.defectos || 0);
                    const totalFaltantes = g.isGroup ? g.movs.reduce((s, x) => s + (x.faltantes || 0), 0) : (m.faltantes || 0);
                    return (
                        <Card
                            key={g.id}
                            className={`card border-l-4 ${isEntrada ? 'border-l-emerald-500' : 'border-l-red-500'} hover:bg-zinc-900/40 transition`}
                            data-testid={`mov-card-${m.id}`}
                        >
                            <CardContent className="p-3 sm:p-4">
                                {/* Header: fecha + tipo + modelo + acciones */}
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        {isEntrada ? (
                                            <ArrowDownCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                                        ) : (
                                            <ArrowUpCircle className="w-5 h-5 text-red-400 shrink-0" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-xs font-semibold uppercase ${isEntrada ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {isEntrada ? 'Entrada' : 'Salida'}
                                                </span>
                                                {g.isGroup && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30" title={`${g.movs.length} tallas en una sola transaccion`}>
                                                        ×{g.movs.length} tallas
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-zinc-500">{m.fecha}</span>
                                            </div>
                                            <p className="text-sm font-medium text-zinc-100 mt-0.5 truncate">
                                                {m.modelo} <span className="text-zinc-400">· {m.color}</span>
                                            </p>
                                            <p className="text-[11px] text-zinc-500 truncate">
                                                {m.corte_id ? getCorteLabel(m.corte_id) : (m.lote_referencia || (m.lote_id ? getLoteNumero(m.lote_id) : 'Sin corte vinculado'))}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Foto + total (visual peso) */}
                                    <div className="flex items-center gap-2 shrink-0">
                                        {m.foto_url ? (
                                            <button
                                                type="button"
                                                onClick={() => { setFotoModal(m); setFotoNuevoFile(null); }}
                                                className="block hover:ring-2 hover:ring-amber-400 rounded transition shrink-0"
                                                title="Ver / cambiar foto"
                                                data-testid={`btn-foto-${m.id}`}
                                            >
                                                <img src={`${API_URL}${m.foto_url}`} alt={m.modelo} className="w-12 h-12 object-cover rounded border border-zinc-700" />
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => { setFotoModal(m); setFotoNuevoFile(null); }}
                                                className="w-12 h-12 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-amber-400 transition shrink-0"
                                                title="Agregar foto"
                                                data-testid={`btn-foto-${m.id}`}
                                            >
                                                <ImageIcon className="w-5 h-5 text-zinc-600" />
                                            </button>
                                        )}
                                        <div className="text-right">
                                            <p className={`text-xl font-bold leading-none ${isEntrada ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {isEntrada ? '+' : '-'}{totalCantidad}
                                            </p>
                                            <p className="text-[10px] text-zinc-500 mt-0.5">pzas</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Detalle por talla con saldo corriente (estilo extracto bancario) */}
                                <div className="bg-zinc-900/60 rounded-md border border-zinc-800 divide-y divide-zinc-800/80 mt-2">
                                    {g.movs.map(mv => {
                                        const saldo = saldoMap[mv.id] || { antes: 0, despues: 0 };
                                        return (
                                            <div key={mv.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                                                <span className="font-medium text-zinc-200 w-12 shrink-0">T-{mv.talla}</span>
                                                <span className={`font-mono shrink-0 ${mv.tipo === 'entrada' ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {mv.tipo === 'entrada' ? '+' : '-'}{mv.cantidad}
                                                </span>
                                                <span className="ml-auto text-zinc-500 font-mono text-[11px]">
                                                    {saldo.antes} <span className="text-zinc-600">→</span>{' '}
                                                    <span className="text-zinc-200 font-semibold">{saldo.despues}</span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Extras (restos / defectos / faltantes) */}
                                {(totalRestos > 0 || totalDefectos > 0 || totalFaltantes > 0) && (
                                    <div className="flex flex-wrap gap-3 mt-2 text-[11px]">
                                        {totalRestos > 0 && <span className="text-zinc-400">Restos: <span className="text-zinc-200">{totalRestos}</span></span>}
                                        {totalDefectos > 0 && <span className="text-orange-400">Defectos: {totalDefectos}</span>}
                                        {totalFaltantes > 0 && <span className="text-red-400">Faltantes: {totalFaltantes}</span>}
                                    </div>
                                )}

                                {/* Acciones */}
                                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-zinc-800">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openEditModal(m)}
                                        className="h-7 px-2 text-blue-400 hover:text-blue-300"
                                        title={g.isGroup ? "Editar primer movimiento del grupo" : "Editar movimiento"}
                                        data-testid={`btn-editar-${m.id}`}
                                    >
                                        <Edit2 className="w-3.5 h-3.5" />
                                    </Button>
                                    {isEntrada && !g.isGroup && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => openAgregarModal(m)}
                                            className="h-7 px-2 text-emerald-400 hover:text-emerald-300"
                                            title="Agregar paquetes a este registro"
                                            data-testid={`btn-agregar-${m.id}`}
                                        >
                                            <PlusCircle className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                    {isEntrada && m.foto_url && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => publicarEnTienda(m)}
                                            className="h-7 px-2 text-amber-400 hover:text-amber-300"
                                            title="Publicar en tienda"
                                            data-testid={`btn-publicar-${m.id}`}
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                    {isAdmin && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => g.isGroup ? handleDeleteGroup(g) : handleDeleteMov(m)}
                                            className="h-7 px-2 text-red-400 hover:text-red-300 ml-auto"
                                            title={g.isGroup ? `Eliminar las ${g.movs.length} tallas del grupo` : "Eliminar movimiento"}
                                            data-testid={`btn-eliminar-${m.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                }) : (
                    <Card className="card"><CardContent className="p-8 text-center text-zinc-400">Sin movimientos</CardContent></Card>
                )}
                {groupedMovs.length > feedLimit && (
                    <div className="flex justify-center pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setFeedLimit(l => l + 20)}
                            className="text-xs"
                            data-testid="btn-ver-mas-feed"
                        >
                            Ver mas ({groupedMovs.length - feedLimit} restantes)
                        </Button>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Dialog open={modalOpen} onOpenChange={(o) => { setModalOpen(o); if (!o) { setForm(initialForm); setResumenCorte(null); setAgregarA(null); setSimilares([]); tallasCantidadesRef.current = {}; } }}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="modal-pt">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {agregarA ? <PlusCircle className="w-5 h-5 text-emerald-400" /> : (form.tipo === 'entrada' ? <ArrowDownCircle className="w-5 h-5 text-emerald-400" /> : <ArrowUpCircle className="w-5 h-5 text-red-400" />)}
                            {agregarA ? `Agregar a ${agregarA.modelo} ${agregarA.talla} ${agregarA.color}` : (form.tipo === 'entrada' ? 'Entrada' : 'Salida') + ' de Producto Terminado'}
                        </DialogTitle>
                    </DialogHeader>
                    {agregarA && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300">
                            <strong>Sumando paquetes</strong> al registro existente del {agregarA.fecha} (actual: {agregarA.cantidad} pzas). Los paquetes se mergean por tamaño.
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* CORTE selector (entrada) */}
                        {form.tipo === 'entrada' && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="form-label">Vincular a Corte</Label>
                                    <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.sin_corte}
                                            onChange={e => setForm({ ...form, sin_corte: e.target.checked, corte_id: '' })}
                                            className="rounded"
                                            data-testid="checkbox-sin-corte"
                                        />
                                        Sin corte vinculado (lote anterior)
                                    </label>
                                </div>
                                {!form.sin_corte ? (
                                    <>
                                        <Select value={form.corte_id} onValueChange={v => setForm({ ...form, corte_id: v })}>
                                            <SelectTrigger data-testid="select-corte-pt"><SelectValue placeholder="Seleccionar corte" /></SelectTrigger>
                                            <SelectContent>
                                                {cortes.map(c => (
                                                    <SelectItem key={c.id} value={c.id}>
                                                        {c.modelo} - {c.color_tela} ({c.relacion_corte}) · Pendiente: {c.pendiente}/{c.piezas_cortadas}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {resumenCorte && (
                                            <div className="p-3 rounded bg-zinc-800/60 border border-zinc-700/50 text-xs space-y-1">
                                                <div className="flex justify-between"><span className="text-zinc-400">Total cortado:</span><span className="font-medium text-zinc-100">{resumenCorte.piezas_cortadas} pzas</span></div>
                                                <div className="flex justify-between"><span className="text-zinc-400">Ya recibido:</span><span className="text-emerald-400">{resumenCorte.total_recibido} pzas</span></div>
                                                <div className="flex justify-between"><span className="text-zinc-400">Pendiente:</span><span className="text-amber-400 font-bold">{resumenCorte.pendiente} pzas</span></div>
                                                {resumenCorte.total_defectos > 0 && (
                                                    <div className="flex justify-between"><span className="text-zinc-400">Defectos acumulados:</span><span className="text-orange-400">{resumenCorte.total_defectos}</span></div>
                                                )}
                                                {resumenCorte.total_faltantes > 0 && (
                                                    <div className="flex justify-between"><span className="text-zinc-400">Faltantes acumulados:</span><span className="text-red-400">{resumenCorte.total_faltantes}</span></div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Input
                                        value={form.lote_referencia}
                                        onChange={e => setForm({ ...form, lote_referencia: e.target.value })}
                                        placeholder='Referencia del lote anterior (ej: "2024 - Lote 87")'
                                        data-testid="input-lote-referencia"
                                    />
                                )}
                            </div>
                        )}

                        {/* FOTO del modelo (entrada) */}
                        {form.tipo === 'entrada' && (
                            <div className="form-group">
                                <Label className="form-label flex items-center gap-2"><Camera className="w-4 h-4" /> Foto del modelo (opcional)</Label>
                                <div className="flex items-center gap-3">
                                    <div className="w-20 h-20 rounded-lg bg-zinc-800/60 border border-zinc-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {form.foto_preview ? (
                                            <img src={form.foto_preview.startsWith('data:') ? form.foto_preview : `${API_URL}${form.foto_preview}`} alt="preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <ImageIcon className="w-8 h-8 text-zinc-600" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFotoChange}
                                            className="hidden"
                                            data-testid="input-foto-pt"
                                        />
                                        <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                                            <UploadIcon className="w-3.5 h-3.5 mr-1" /> {form.foto_preview ? 'Cambiar foto' : 'Subir foto'}
                                        </Button>
                                        {form.foto_file && (
                                            <p className="text-xs text-zinc-500 mt-1 truncate">{form.foto_file.name}</p>
                                        )}
                                        {!form.sin_corte && form.corte_id && (
                                            <p className="text-xs text-zinc-500 mt-1">La foto se asocia al CORTE y se hereda en futuras entradas.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {form.tipo === 'entrada' ? (
                            <div className="grid grid-cols-2 gap-3">
                                <div className="form-group"><Label className="form-label">Modelo *</Label>
                                    <Input value={form.modelo} onChange={e => setForm({ ...form, modelo: e.target.value })} placeholder="Skinny 501" data-testid="input-modelo-pt" />
                                </div>
                                <div className="form-group"><Label className="form-label">Color *</Label>
                                    <Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="Azul Oscuro" data-testid="input-color-pt" />
                                </div>
                            </div>
                        ) : (
                            <div className="form-group space-y-3">
                                {/* === Selector de TIPO DE SALIDA (Unidad / Paquete / Corrida) === */}
                                <div>
                                    <Label className="form-label">Tipo de salida *</Label>
                                    <div className="grid grid-cols-3 gap-2 mt-1">
                                        {[
                                            { id: 'unidad', label: 'Por unidad', sub: '1 talla especifica' },
                                            { id: 'paquete', label: 'Paquete', sub: 'Surtido completo' },
                                            { id: 'corrida', label: 'Corrida', sub: 'Mitad del paquete' },
                                        ].map(opt => (
                                            <button
                                                type="button"
                                                key={opt.id}
                                                onClick={() => {
                                                    setForm(f => {
                                                        // Cambio a UNIDAD: limpiar todo (requiere talla especifica)
                                                        if (opt.id === 'unidad') {
                                                            return {
                                                                ...f,
                                                                salida_modo: 'unidad',
                                                                inv_key: '',
                                                                talla: '',
                                                                modelo: '',
                                                                color: '',
                                                                salida_search: '',
                                                                salida_paquetes: '',
                                                                cantidad: '',
                                                                stock_disponible: 0,
                                                                salida_piezas_por_paquete: 0,
                                                            };
                                                        }
                                                        // Cambio entre PAQUETE <-> CORRIDA: preservar modelo+color+composicion
                                                        // y recalcular piezas_por_paquete si tenemos modelo+color
                                                        const k = `${f.modelo || ''}|${f.color || ''}`;
                                                        const paqInfo = paquetesByModelo[k];
                                                        const piezasPorPaq = paqInfo?.piezas_por_paquete || f.salida_piezas_por_paquete || 0;
                                                        return {
                                                            ...f,
                                                            salida_modo: opt.id,
                                                            inv_key: '',
                                                            talla: '',
                                                            salida_piezas_por_paquete: piezasPorPaq,
                                                            // Mantener salida_paquetes si ya hay valor, sino '1'
                                                            salida_paquetes: f.salida_paquetes || (f.modelo && f.color ? '1' : ''),
                                                            cantidad: '0',
                                                        };
                                                    });
                                                }}
                                                className={`p-2 rounded-md border text-left transition ${form.salida_modo === opt.id ? 'border-amber-400 bg-amber-400/10' : 'border-zinc-700 bg-zinc-800/40 hover:border-zinc-500'}`}
                                                data-testid={`btn-modo-salida-${opt.id}`}
                                            >
                                                <p className={`text-xs font-bold ${form.salida_modo === opt.id ? 'text-amber-300' : 'text-zinc-200'}`}>{opt.label}</p>
                                                <p className="text-[10px] text-zinc-500 leading-tight">{opt.sub}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* === BUSCADOR: cambia segun el modo === */}
                                {(form.salida_modo === 'paquete' || form.salida_modo === 'corrida') ? (
                                    <div>
                                        <Label className="form-label">Modelo · Color a dar salida *</Label>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                            <Input
                                                value={form.salida_search}
                                                onChange={(e) => setForm({ ...form, salida_search: e.target.value, modelo: '', color: '' })}
                                                placeholder="Escribe modelo o color..."
                                                className="pl-9"
                                                data-testid="input-buscar-salida-modelo"
                                            />
                                        </div>
                                        {form.salida_search && !(form.modelo && form.color) && (() => {
                                            // Agrupar inventario por modelo+color con composicion y stock total
                                            const term = form.salida_search.toLowerCase();
                                            const grupos = {};
                                            for (const it of inventario) {
                                                if ((it.stock || 0) <= 0) continue;
                                                const k = `${it.modelo}|${it.color}`;
                                                if (!grupos[k]) {
                                                    grupos[k] = { modelo: it.modelo, color: it.color, stockTotal: 0, tallas: {} };
                                                }
                                                grupos[k].stockTotal += (it.stock || 0);
                                                grupos[k].tallas[it.talla] = (grupos[k].tallas[it.talla] || 0) + (it.stock || 0);
                                            }
                                            const lista = Object.values(grupos).filter(g => {
                                                const paqInfo = paquetesByModelo[`${g.modelo}|${g.color}`];
                                                // Solo mostrar si tiene composicion de paquete registrada
                                                if (!paqInfo || !paqInfo.composicion) return false;
                                                return (
                                                    (g.modelo || '').toLowerCase().includes(term) ||
                                                    (g.color || '').toLowerCase().includes(term)
                                                );
                                            }).slice(0, 8);
                                            if (lista.length === 0) {
                                                return <p className="text-xs text-zinc-500 mt-2">Sin modelos con paquetes registrados</p>;
                                            }
                                            return (
                                                <div className="mt-2 max-h-[260px] overflow-y-auto border border-zinc-700 rounded-md divide-y divide-zinc-800/80" data-testid="lista-resultados-salida-paquete">
                                                    {lista.map((g, i) => {
                                                        const paqInfo = paquetesByModelo[`${g.modelo}|${g.color}`];
                                                        const piezasPorPaq = paqInfo?.piezas_por_paquete || 0;
                                                        return (
                                                            <button
                                                                key={`${g.modelo}|${g.color}`}
                                                                type="button"
                                                                onClick={() => {
                                                                    setForm(f => ({
                                                                        ...f,
                                                                        modelo: g.modelo,
                                                                        color: g.color,
                                                                        talla: '',
                                                                        inv_key: '',
                                                                        stock_disponible: g.stockTotal,
                                                                        salida_piezas_por_paquete: piezasPorPaq,
                                                                        salida_paquetes: '1',
                                                                        cantidad: '0',
                                                                    }));
                                                                }}
                                                                className="w-full text-left px-3 py-2 hover:bg-zinc-800 transition"
                                                                data-testid={`resultado-salida-paq-${i}`}
                                                            >
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <p className="text-sm text-zinc-100 font-medium truncate">{g.modelo} <span className="text-zinc-400">· {g.color}</span></p>
                                                                    <span className="text-sm font-bold text-amber-400 shrink-0">{g.stockTotal} pzas</span>
                                                                </div>
                                                                <p className="text-[11px] text-zinc-500">
                                                                    Paquete de {piezasPorPaq} pzs · {Object.keys(paqInfo?.composicion || {}).length} tallas surtidas
                                                                </p>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                        {(form.modelo && form.color && (form.salida_modo === 'paquete' || form.salida_modo === 'corrida')) && (() => {
                                            const paqInfo = paquetesByModelo[`${form.modelo}|${form.color}`];
                                            const piezasPorPaq = paqInfo?.piezas_por_paquete || 0;
                                            const composicion = paqInfo?.composicion || {};
                                            return (
                                                <div className="mt-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/30 space-y-2" data-testid="seleccion-modelo-paquete">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-sm text-emerald-300">
                                                            ✓ <strong>{form.modelo}</strong> · {form.color} (Stock total: {form.stock_disponible} pzas)
                                                        </span>
                                                        <button type="button" onClick={() => setForm(f => ({ ...f, modelo: '', color: '', salida_search: '', salida_paquetes: '', cantidad: '' }))} className="text-zinc-400 hover:text-zinc-200">
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        <span className="text-[11px] text-zinc-400 mr-1">Paquete de {piezasPorPaq} pzs:</span>
                                                        {Object.entries(composicion).map(([t, c]) => (
                                                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-zinc-300">
                                                                {c}× T-{t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div>
                                        <Label className="form-label">Producto en inventario *</Label>
                                        {/* Buscador con autocomplete (por talla) */}
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                            <Input
                                                value={form.salida_search}
                                                onChange={(e) => setForm({ ...form, salida_search: e.target.value, inv_key: '' })}
                                                placeholder="Escribe modelo, color o talla..."
                                                className="pl-9"
                                                data-testid="input-buscar-salida"
                                            />
                                        </div>
                                        {form.salida_search && !form.inv_key && (() => {
                                            const term = form.salida_search.toLowerCase();
                                            const matches = inventario.filter(i =>
                                                i.stock > 0 && (
                                                    (i.modelo || '').toLowerCase().includes(term) ||
                                                    (i.color || '').toLowerCase().includes(term) ||
                                                    (i.talla || '').toLowerCase().includes(term)
                                                )
                                            ).slice(0, 8);
                                            if (matches.length === 0) {
                                                return <p className="text-xs text-zinc-500 mt-2">Sin resultados</p>;
                                            }
                                            return (
                                                <div className="mt-2 max-h-[280px] overflow-y-auto border border-zinc-700 rounded-md divide-y divide-zinc-800/80" data-testid="lista-resultados-salida">
                                                    {matches.map((item, i) => {
                                                        const k = `${item.modelo}|${item.talla}|${item.color}`;
                                                        // Tomar piezas/paquete de la composicion del modelo+color
                                                        const paqInfo = paquetesByModelo[`${item.modelo}|${item.color}`];
                                                        const piezasPorPaqTalla = paqInfo?.composicion?.[item.talla] || 0;
                                                        return (
                                                            <button
                                                                key={k}
                                                                type="button"
                                                                onClick={() => {
                                                                    setForm(f => ({
                                                                        ...f,
                                                                        inv_key: k,
                                                                        modelo: item.modelo,
                                                                        talla: item.talla,
                                                                        color: item.color,
                                                                        stock_disponible: item.stock,
                                                                        salida_piezas_por_paquete: piezasPorPaqTalla,
                                                                        salida_modo: 'unidad',
                                                                        salida_paquetes: '',
                                                                        cantidad: ''
                                                                    }));
                                                                }}
                                                                className="w-full text-left px-3 py-2 hover:bg-zinc-800 transition flex items-center justify-between gap-2"
                                                                data-testid={`resultado-salida-${i}`}
                                                            >
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-sm text-zinc-100 font-medium truncate">{item.modelo} <span className="text-zinc-400">· {item.color}</span></p>
                                                                    <p className="text-[11px] text-zinc-500">Talla {item.talla}{piezasPorPaqTalla > 0 ? ` · ${piezasPorPaqTalla} pzas/paq` : ''}</p>
                                                                </div>
                                                                <span className="text-sm font-bold text-amber-400 shrink-0">{item.stock} pzas</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                        {form.inv_key && (
                                            <div className="mt-2 p-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs flex items-center justify-between">
                                                <span className="text-emerald-300">
                                                    ✓ <strong>{form.modelo}</strong> · T-{form.talla} · {form.color} ({form.stock_disponible} pzas)
                                                </span>
                                                <button type="button" onClick={() => setForm(f => ({ ...f, inv_key: '', talla: '', cantidad: '', salida_paquetes: '', salida_search: '' }))} className="text-zinc-400 hover:text-zinc-200">
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Sugerencia de registros similares (solo en entrada nueva) */}
                        {form.tipo === 'entrada' && !agregarA && similares.length > 0 && (
                            <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/40 space-y-2" data-testid="similares-banner">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                                    <div className="text-xs text-amber-200">
                                        Ya existe{similares.length > 1 ? 'n' : ''} <strong>{similares.length}</strong> registro{similares.length > 1 ? 's' : ''} con estas caracteristicas. ¿Quieres sumar a uno existente en lugar de crear uno nuevo?
                                    </div>
                                </div>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                    {similares.slice(0, 5).map((s) => (
                                        <button
                                            type="button"
                                            key={s.id}
                                            onClick={() => openAgregarModal(s)}
                                            className="w-full text-left p-2 rounded bg-zinc-800/60 hover:bg-emerald-500/20 border border-zinc-700 hover:border-emerald-500/50 text-xs flex items-center justify-between gap-2 transition"
                                            data-testid={`btn-similar-${s.id}`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                {s.foto_url && <img src={`${API_URL}${s.foto_url}`} alt="" className="w-8 h-8 object-cover rounded shrink-0" />}
                                                <div className="min-w-0">
                                                    <p className="text-zinc-100 font-medium">{s.fecha} · {s.cantidad} pzas</p>
                                                    <p className="text-zinc-500 text-[10px] truncate">
                                                        {s.corte_id ? getCorteLabel(s.corte_id) : (s.lote_referencia || 'Sin lote')}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="text-emerald-400 text-[10px] shrink-0 flex items-center gap-1">
                                                <PlusCircle className="w-3 h-3" /> Sumar a este
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* TABLA DE TALLAS (solo entrada, modo multi-talla).
                            En modo "Agregar a existente" se muestra con la composicion congelada
                            (talla y cantidad-por-paquete read-only). */}
                        {form.tipo === 'entrada' && (
                            <Card className="card bg-zinc-800/30">
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <Label className="form-label flex items-center gap-2">
                                            <Package className="w-4 h-4" /> Tallas recibidas
                                            {tallasDelCorte.length > 0 && <span className="text-[10px] text-emerald-400">(del corte)</span>}
                                            {form.sin_corte && !agregarA && <span className="text-[10px] text-amber-400">(estandar)</span>}
                                            {agregarA && <span className="text-[10px] text-amber-400">(composicion del registro original)</span>}
                                        </Label>
                                        <div className="flex items-center gap-2">
                                            {form.sin_corte && !agregarA && (
                                                <div className="inline-flex bg-zinc-800 rounded-md p-0.5 border border-zinc-700">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSegmentoTallas('adulto')}
                                                        className={`px-3 py-1 rounded-md text-xs font-medium transition ${segmentoTallas === 'adulto' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
                                                        data-testid="btn-pt-talla-adulto"
                                                    >Adulto</button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSegmentoTallas('nino')}
                                                        className={`px-3 py-1 rounded-md text-xs font-medium transition ${segmentoTallas === 'nino' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
                                                        data-testid="btn-pt-talla-nino"
                                                    >Niño</button>
                                                </div>
                                            )}
                                            {!agregarA && (
                                                <Button type="button" size="sm" onClick={addFilaTalla} className="bg-amber-400 hover:bg-amber-500 text-zinc-900 h-8" data-testid="btn-add-talla">
                                                    <Plus className="w-3.5 h-3.5 mr-1" /> Agregar talla
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Multiplicador de paquetes recibidos */}
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-400/10 border border-amber-400/30">
                                        <div className="flex-1">
                                            <Label className="text-xs text-amber-300 font-semibold">Paquetes recibidos *</Label>
                                            <p className="text-[10px] text-zinc-400 mt-0.5">Cada paquete trae las tallas listadas. La cantidad se multiplica al guardar.</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-9 w-9 p-0"
                                                onClick={() => setForm(f => ({ ...f, paquetesRecibidos: Math.max(1, (parseInt(f.paquetesRecibidos) || 1) - 1) }))}
                                                data-testid="btn-paquetes-menos"
                                            >−</Button>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={form.paquetesRecibidos}
                                                onChange={(e) => setForm({ ...form, paquetesRecibidos: parseInt(e.target.value) || 1 })}
                                                className="h-9 w-20 text-center font-bold text-lg"
                                                data-testid="input-paquetes-recibidos"
                                            />
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-9 w-9 p-0"
                                                onClick={() => setForm(f => ({ ...f, paquetesRecibidos: (parseInt(f.paquetesRecibidos) || 1) + 1 }))}
                                                data-testid="btn-paquetes-mas"
                                            >+</Button>
                                            <span className="text-xs text-zinc-400 ml-1">paq.</span>
                                        </div>
                                    </div>
                                    {(form.filasTallas || []).length === 0 ? (
                                        <p className="text-xs text-zinc-500 italic">
                                            {form.corte_id ? 'Selecciona el corte para prellenar las tallas, o agrega manualmente.' : 'Agrega las tallas que recibiste con su cantidad.'}
                                        </p>
                                    ) : (
                                        <div className="space-y-2">
                                            {form.filasTallas.map((fila, idx) => (
                                                <div key={idx} className="flex items-end gap-2" data-testid={`fila-talla-${idx}`}>
                                                    <div className="flex-1">
                                                        <Label className="text-xs text-zinc-400">Talla</Label>
                                                        {tallasDelCorte.length > 0 ? (
                                                            <Select value={fila.talla || ''} onValueChange={v => updateFilaTalla(idx, 'talla', v)} disabled={!!agregarA}>
                                                                <SelectTrigger className="h-9"><SelectValue placeholder="Talla" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {tallasDelCorte.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                                                </SelectContent>
                                                            </Select>
                                                        ) : (
                                                            <Input
                                                                value={fila.talla || ''}
                                                                onChange={e => updateFilaTalla(idx, 'talla', e.target.value)}
                                                                placeholder="30"
                                                                className="h-9"
                                                                disabled={!!agregarA}
                                                                data-testid={`input-talla-${idx}`}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="flex-[2]">
                                                        <Label className="text-xs text-zinc-400">{agregarA ? 'Piezas/paq (fijo)' : 'Piezas'}</Label>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            value={fila.cantidad}
                                                            onChange={e => updateFilaTalla(idx, 'cantidad', e.target.value)}
                                                            className="h-9"
                                                            placeholder="0"
                                                            disabled={!!agregarA}
                                                            data-testid={`input-cant-talla-${idx}`}
                                                        />
                                                    </div>
                                                    {!agregarA && (
                                                        <Button type="button" size="sm" variant="ghost" onClick={() => removeFilaTalla(idx)} className="text-red-400 hover:text-red-300 h-9 px-2">
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                            <p className="text-right text-xs text-zinc-300 pt-2 border-t border-zinc-700/50">
                                                {(parseInt(form.paquetesRecibidos) || 1) > 1 ? (
                                                    <>Subtotal: <span className="text-zinc-400">{totalFilasTallasBruto} × {form.paquetesRecibidos} paq</span> = <span className="font-bold text-amber-400">{totalFilasTallas}</span> piezas</>
                                                ) : (
                                                    <>Subtotal tallas: <span className="font-bold text-amber-400">{totalFilasTallas}</span> piezas</>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* PAQUETES legacy (solo si es agregarA a un registro existente) */}
                        {form.tipo === 'entrada' && agregarA && (
                            <Card className="card bg-zinc-800/30">
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Label className="form-label flex items-center gap-2">
                                            <Package className="w-4 h-4" /> Paquetes a agregar
                                        </Label>
                                        <Button type="button" size="sm" onClick={addPaquete} className="bg-amber-400 hover:bg-amber-500 text-zinc-900 h-8" data-testid="btn-add-paquete">
                                            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
                                        </Button>
                                    </div>
                                    {form.paquetes.length === 0 ? (
                                        <p className="text-xs text-zinc-500 italic">Aun no agregaste paquetes. Si todo viene suelto, registralo en "Restos" abajo.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {form.paquetes.map((p, idx) => (
                                                <div key={idx} className="flex items-end gap-2" data-testid={`paquete-row-${idx}`}>
                                                    <div className="flex-1">
                                                        <Label className="text-xs text-zinc-400">Piezas/paquete</Label>
                                                        <Select value={String(p.piezas_por_paquete)} onValueChange={v => updatePaquete(idx, 'piezas_por_paquete', v)}>
                                                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                {PIEZAS_POR_PAQUETE_OPCIONES.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="flex-1">
                                                        <Label className="text-xs text-zinc-400">Cantidad</Label>
                                                        <Input type="number" min="1" value={p.cantidad_paquetes} onChange={e => updatePaquete(idx, 'cantidad_paquetes', e.target.value)} className="h-9" />
                                                    </div>
                                                    <div className="text-xs text-zinc-400 px-2 pb-2 whitespace-nowrap">
                                                        = <span className="text-amber-400 font-medium">{(parseInt(p.piezas_por_paquete) || 0) * (parseInt(p.cantidad_paquetes) || 0)}</span>
                                                    </div>
                                                    <Button type="button" size="sm" variant="ghost" onClick={() => removePaquete(idx)} className="text-red-400 hover:text-red-300 h-9 px-2">
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                            <p className="text-right text-xs text-zinc-300 pt-2 border-t border-zinc-700/50">
                                                Subtotal: <span className="font-bold text-amber-400">{totalPaquetes}</span> piezas
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Restos / Defectos / Faltantes (solo entrada) */}
                        {form.tipo === 'entrada' && (
                            <div className="grid grid-cols-3 gap-3">
                                <div className="form-group"><Label className="form-label flex items-center gap-1">Restos <span className="text-xs text-zinc-500">(huerfanos)</span></Label>
                                    <Input type="number" min="0" value={form.restos} onChange={e => setForm({ ...form, restos: e.target.value })} placeholder="0" data-testid="input-restos-pt" />
                                </div>
                                <div className="form-group"><Label className="form-label">Defectos</Label>
                                    <Input type="number" min="0" value={form.defectos} onChange={e => setForm({ ...form, defectos: e.target.value })} placeholder="0" data-testid="input-defectos-pt" />
                                </div>
                                <div className="form-group"><Label className="form-label">Faltantes</Label>
                                    <Input type="number" min="0" value={form.faltantes} onChange={e => setForm({ ...form, faltantes: e.target.value })} placeholder="0" data-testid="input-faltantes-pt" />
                                </div>
                            </div>
                        )}

                        {/* Cantidad (solo salida) - se adapta al modo (unidad / paquete / corrida) */}
                        {form.tipo === 'salida' && (
                            <div className="form-group">
                                <Label className="form-label">
                                    {form.salida_modo === 'paquete' ? 'Cantidad de paquetes a dar salida *'
                                        : form.salida_modo === 'corrida' ? 'Cantidad de corridas a dar salida *'
                                            : 'Cantidad a dar salida *'}
                                </Label>
                                {(form.salida_modo === 'paquete' || form.salida_modo === 'corrida') ? (
                                    (() => {
                                        const piezasPorPaq = parseInt(form.salida_piezas_por_paquete) || 0;
                                        const piezasPorUnidad = form.salida_modo === 'corrida'
                                            ? Math.floor(piezasPorPaq / 2)
                                            : piezasPorPaq;
                                        const n = parseInt(form.salida_paquetes) || 0;
                                        const totalCalc = form.salida_modo === 'corrida'
                                            ? Math.floor((piezasPorPaq * n) / 2)
                                            : piezasPorPaq * n;
                                        const unidadLabel = form.salida_modo === 'corrida' ? 'corrida' : 'paquete';
                                        const seleccionado = !!(form.modelo && form.color);
                                        return (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={form.salida_paquetes}
                                                        onChange={e => {
                                                            setForm(f => ({
                                                                ...f,
                                                                salida_paquetes: e.target.value,
                                                                cantidad: String(totalCalc),
                                                            }));
                                                        }}
                                                        placeholder="0"
                                                        className="flex-1 min-w-[80px]"
                                                        data-testid="input-salida-paquetes"
                                                        disabled={!seleccionado}
                                                    />
                                                    <span className="text-xs text-zinc-400">× ~{piezasPorUnidad} pzas/{unidadLabel}</span>
                                                    <span className="text-sm font-bold text-amber-400">≈ {totalCalc} pzas</span>
                                                </div>
                                                {form.salida_modo === 'corrida' && piezasPorPaq > 0 && (
                                                    <p className="text-[10px] text-zinc-500">
                                                        Corrida = mitad del paquete de {piezasPorPaq} pzs ({piezasPorUnidad} pzs aprox.). El surtido se descuenta automaticamente de las tallas con sobrante distribuido en las de mayor cantidad.
                                                    </p>
                                                )}
                                                {form.salida_modo === 'paquete' && piezasPorPaq > 0 && (
                                                    <p className="text-[10px] text-zinc-500">
                                                        Se descontara el paquete completo surtido ({piezasPorPaq} pzs) en sus tallas correspondientes.
                                                    </p>
                                                )}
                                                {!seleccionado && (
                                                    <p className="text-[11px] text-amber-400">Selecciona primero el modelo + color en el buscador.</p>
                                                )}
                                            </div>
                                        );
                                    })()
                                ) : (
                                    <Input
                                        type="number"
                                        min="1"
                                        max={form.stock_disponible || undefined}
                                        value={form.cantidad}
                                        onChange={e => setForm({ ...form, cantidad: e.target.value })}
                                        data-testid="input-cantidad-pt"
                                        disabled={!form.inv_key}
                                    />
                                )}
                                {form.salida_modo === 'unidad' && form.stock_disponible > 0 && form.cantidad && parseInt(form.cantidad) > form.stock_disponible && (
                                    <p className="text-xs text-red-400 mt-1">Excede el stock disponible ({form.stock_disponible})</p>
                                )}
                            </div>
                        )}

                        {/* Total destacado (solo entrada) */}
                        {form.tipo === 'entrada' && (() => {
                            const filasOK = (form.filasTallas || []).filter(r => (r.talla || '').trim() !== '' && (parseInt(r.cantidad) || 0) > 0);
                            const esMulti = filasOK.length > 0;
                            const total = esMulti ? (totalFilasTallas + (parseInt(form.restos) || 0)) : totalEntrada;
                            return (
                                <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-3 flex justify-between items-center">
                                    <span className="text-sm text-zinc-300">
                                        Total a registrar como ENTRADA
                                        {esMulti && <span className="text-xs text-emerald-400 ml-2">({filasOK.length} talla{filasOK.length > 1 ? 's' : ''} · 1 registro c/u)</span>}
                                        :
                                    </span>
                                    <span className="text-2xl font-bold text-amber-400" data-testid="total-entrada-pt">{total} pzas</span>
                                </div>
                            );
                        })()}

                        {/* Aviso si excede pendiente del corte */}
                        {form.tipo === 'entrada' && resumenCorte && totalEntrada > resumenCorte.pendiente && resumenCorte.pendiente > 0 && (
                            <div className="flex items-start gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>Estas registrando mas piezas ({totalEntrada}) que las que faltan del corte ({resumenCorte.pendiente}). Verifica.</span>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="form-group"><Label className="form-label">Fecha</Label>
                                <Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
                            </div>
                            <div className="form-group"><Label className="form-label">Lote (opcional)</Label>
                                <Select value={form.lote_id} onValueChange={v => setForm({ ...form, lote_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{lotes.map(l => <SelectItem key={l.id} value={l.id}>{l.numero_lote}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="form-group"><Label className="form-label">Observaciones</Label>
                            <Input value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} placeholder="Notas" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={submitting} className={form.tipo === 'entrada' ? 'bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60' : 'bg-red-500 hover:bg-red-600 disabled:opacity-60'} data-testid="submit-pt">
                                {submitting ? 'Guardando...' : (agregarA ? 'Agregar al registro' : `Registrar ${form.tipo === 'entrada' ? 'Entrada' : 'Salida'}`)}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal: Foto del producto */}
            <Dialog open={!!fotoModal} onOpenChange={(o) => { if (!o) { setFotoModal(null); setFotoNuevoFile(null); } }}>
                <DialogContent className="sm:max-w-[500px]" data-testid="modal-foto-pt">
                    <DialogHeader>
                        <DialogTitle>Foto del producto · {fotoModal?.modelo} {fotoModal?.talla} {fotoModal?.color}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            {fotoModal?.foto_url ? (
                                <img src={`${API_URL}${fotoModal.foto_url}`} alt={fotoModal.modelo} className="max-h-[400px] rounded-lg border border-zinc-700 object-contain" />
                            ) : (
                                <div className="w-48 h-48 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                                    <ImageIcon className="w-12 h-12 text-zinc-600" />
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label className="form-label">{fotoModal?.foto_url ? 'Cambiar por nueva foto' : 'Agregar foto'}</Label>
                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setFotoNuevoFile(e.target.files?.[0] || null)}
                                data-testid="input-foto-nueva"
                            />
                            {fotoNuevoFile && <p className="text-xs text-emerald-400">Lista para subir: {fotoNuevoFile.name}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => { setFotoModal(null); setFotoNuevoFile(null); }}>Cerrar</Button>
                        <Button type="button" disabled={!fotoNuevoFile} onClick={handleFotoUpload} className="bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold" data-testid="btn-subir-foto">
                            <Camera className="w-4 h-4 mr-2" /> Guardar foto
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal: Info completa del producto (click en tarjeta del inventario) */}
            <Dialog open={!!infoInv} onOpenChange={(o) => { if (!o) setInfoInv(null); }}>
                <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto" data-testid="modal-info-inv">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-amber-400" />
                            {infoInv?.modelo}
                            {infoInv?.talla ? <> · <span className="text-amber-400">T-{infoInv.talla}</span></> : <span className="text-zinc-500 text-sm font-normal ml-1">· Todas las tallas</span>}
                            {' · '}{infoInv?.color}
                        </DialogTitle>
                    </DialogHeader>
                    {infoInv && (() => {
                        // Filtrar movimientos: si hay talla seleccionada, solo esa; si no, todas las tallas del modelo+color
                        const matchTalla = (m) => infoInv.talla ? m.talla === infoInv.talla : true;
                        const movsProducto = movimientos.filter(m => m.modelo === infoInv.modelo && m.color === infoInv.color && matchTalla(m));
                        const entradas = movsProducto.filter(m => m.tipo === 'entrada');
                        const salidas = movsProducto.filter(m => m.tipo === 'salida');
                        const totalEntradas = entradas.reduce((s, m) => s + (m.cantidad || 0), 0);
                        const totalSalidas = salidas.reduce((s, m) => s + (m.cantidad || 0), 0);
                        const totalDefectos = entradas.reduce((s, m) => s + (m.defectos || 0), 0);
                        const totalFaltantes = entradas.reduce((s, m) => s + (m.faltantes || 0), 0);
                        const totalRestos = entradas.reduce((s, m) => s + (m.restos || 0), 0);
                        const ultimaEntrada = entradas.length > 0 ? entradas.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))[0] : null;
                        const ultimaSalida = salidas.length > 0 ? salidas.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))[0] : null;
                        // Resumen por talla del MISMO modelo+color (todas las tallas)
                        const todasMismoModelo = inventario.filter(i => i.modelo === infoInv.modelo && i.color === infoInv.color);
                        const totalModeloColor = todasMismoModelo.reduce((s, i) => s + (i.stock || 0), 0);
                        const stockMostrado = infoInv.talla
                            ? (todasMismoModelo.find(t => t.talla === infoInv.talla)?.stock || 0)
                            : totalModeloColor;
                        // Cortes vinculados
                        const cortesIds = [...new Set(entradas.map(m => m.corte_id).filter(Boolean))];
                        const cortesVinculados = cortes.filter(c => cortesIds.includes(c.id));
                        // Lotes/referencias
                        const lotesRefs = [...new Set(entradas.map(m => m.lote_referencia).filter(Boolean))];
                        // Orden natural de tallas
                        const sortTallas = (a, b) => {
                            const na = parseFloat(a.talla); const nb = parseFloat(b.talla);
                            if (!isNaN(na) && !isNaN(nb)) return na - nb;
                            if (!isNaN(na)) return -1;
                            if (!isNaN(nb)) return 1;
                            return (a.talla || '').localeCompare(b.talla || '');
                        };
                        return (
                            <div className="space-y-4">
                                <div className="flex gap-4 items-start flex-wrap sm:flex-nowrap">
                                    {infoInv.foto_url ? (
                                        <img src={`${API_URL}${infoInv.foto_url}`} alt={infoInv.modelo} className="w-48 h-48 rounded-lg object-cover border border-zinc-700 shrink-0" />
                                    ) : (
                                        <div className="w-48 h-48 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0">
                                            <ImageIcon className="w-12 h-12 text-zinc-600" />
                                        </div>
                                    )}
                                    <div className="flex-1 space-y-2 min-w-0">
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/30">
                                                <p className="text-[10px] text-emerald-400">Entradas</p>
                                                <p className="text-xl font-bold text-emerald-400">{totalEntradas}</p>
                                                <p className="text-[10px] text-zinc-500">{entradas.length} mov.</p>
                                            </div>
                                            <div className="p-2 rounded bg-red-500/10 border border-red-500/30">
                                                <p className="text-[10px] text-red-400">Salidas</p>
                                                <p className="text-xl font-bold text-red-400">{totalSalidas}</p>
                                                <p className="text-[10px] text-zinc-500">{salidas.length} mov.</p>
                                            </div>
                                            <div className="p-2 rounded bg-amber-400/10 border border-amber-400/30">
                                                <p className="text-[10px] text-amber-400">Stock</p>
                                                <p className="text-xl font-bold text-amber-400">{stockMostrado}</p>
                                                <p className="text-[10px] text-zinc-500">pzas</p>
                                            </div>
                                        </div>
                                        {/* Defectos / Faltantes / Restos */}
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="p-2 rounded bg-zinc-800/60 border border-zinc-700">
                                                <p className="text-[10px] text-zinc-500">Defectos</p>
                                                <p className="text-sm font-bold text-orange-400">{totalDefectos}</p>
                                            </div>
                                            <div className="p-2 rounded bg-zinc-800/60 border border-zinc-700">
                                                <p className="text-[10px] text-zinc-500">Faltantes</p>
                                                <p className="text-sm font-bold text-red-400">{totalFaltantes}</p>
                                            </div>
                                            <div className="p-2 rounded bg-zinc-800/60 border border-zinc-700">
                                                <p className="text-[10px] text-zinc-500">Restos</p>
                                                <p className="text-sm font-bold text-zinc-300">{totalRestos}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 pt-2 flex-wrap">
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    const paqInfo = paquetesByModelo[`${infoInv.modelo}|${infoInv.color}`];
                                                    const tieneTallaSel = !!infoInv.talla;
                                                    const tallaInfo = tieneTallaSel ? todasMismoModelo.find(t => t.talla === infoInv.talla) : null;
                                                    const piezasPorPaqTalla = tieneTallaSel ? (paqInfo?.composicion?.[infoInv.talla] || 0) : 0;
                                                    const piezasPorPaqTotal = paqInfo?.piezas_por_paquete || 0;
                                                    setInfoInv(null);
                                                    setForm({
                                                        ...initialForm,
                                                        tipo: 'salida',
                                                        modelo: infoInv.modelo,
                                                        talla: tieneTallaSel ? infoInv.talla : '',
                                                        color: infoInv.color,
                                                        inv_key: tieneTallaSel ? `${infoInv.modelo}|${infoInv.talla}|${infoInv.color}` : '',
                                                        stock_disponible: tieneTallaSel ? (tallaInfo?.stock || 0) : (stockMostrado || 0),
                                                        salida_piezas_por_paquete: tieneTallaSel ? piezasPorPaqTalla : piezasPorPaqTotal,
                                                        // Si hay talla -> modo unidad; sino y hay paquetes registrados -> modo paquete
                                                        salida_modo: tieneTallaSel ? 'unidad' : (piezasPorPaqTotal > 0 ? 'paquete' : 'unidad'),
                                                        salida_paquetes: !tieneTallaSel && piezasPorPaqTotal > 0 ? '1' : '',
                                                    });
                                                    setModalOpen(true);
                                                }}
                                                className="bg-red-500 hover:bg-red-600"
                                                data-testid="btn-salida-rapida"
                                                title={infoInv.talla ? `Dar salida desde T-${infoInv.talla}` : 'Dar salida (paquete/corrida surtidos o por unidad)'}
                                            >
                                                <ArrowUpCircle className="w-4 h-4 mr-1" /> Dar salida
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setInfoInv(null);
                                                    setForm({
                                                        ...initialForm,
                                                        tipo: 'entrada',
                                                        sin_corte: true,
                                                        modelo: infoInv.modelo,
                                                        color: infoInv.color,
                                                        filasTallas: infoInv.talla ? [{ talla: infoInv.talla, cantidad: '' }] : [{ talla: '', cantidad: '' }]
                                                    });
                                                    setModalOpen(true);
                                                }}
                                            >
                                                <ArrowDownCircle className="w-4 h-4 mr-1" /> Nueva entrada
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Paquetes registrados (info, no editable) */}
                                {(() => {
                                    const paqInfo = paquetesByModelo[`${infoInv.modelo}|${infoInv.color}`];
                                    if (!paqInfo || paqInfo.total_paquetes <= 0) return null;
                                    const compEntries = Object.entries(paqInfo.composicion || {})
                                        .sort(([a], [b]) => sortTallas({ talla: a }, { talla: b }));
                                    return (
                                        <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/30" data-testid="modal-paquetes-info">
                                            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                                                <p className="text-xs font-semibold text-amber-300 flex items-center gap-2">
                                                    <Package className="w-4 h-4" /> Paquetes registrados
                                                </p>
                                                <span className="text-lg font-bold text-amber-400">
                                                    {paqInfo.total_paquetes} <span className="text-xs font-normal text-zinc-400">paq · {paqInfo.piezas_por_paquete} pzas/paq</span>
                                                </span>
                                            </div>
                                            {compEntries.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5">
                                                    <span className="text-[10px] text-zinc-400 uppercase tracking-wide self-center">Composicion:</span>
                                                    {compEntries.map(([talla, cant]) => (
                                                        <span key={talla} className="text-[11px] px-2 py-0.5 rounded bg-zinc-800/60 border border-zinc-700 text-zinc-200">
                                                            {cant}× T-{talla}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Resumen por talla del modelo+color (chips clickeables para filtrar) */}
                                {todasMismoModelo.length > 0 && (
                                    <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700">
                                        <p className="text-xs font-semibold text-zinc-300 mb-2">
                                            Stock total del modelo "<span className="text-amber-400">{infoInv.modelo} {infoInv.color}</span>" en todas las tallas:
                                            <span className="text-amber-400 ml-2">{totalModeloColor} pzas</span>
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => setInfoInv({ ...infoInv, talla: null })}
                                                className={`text-xs px-2 py-1 rounded transition ${!infoInv.talla ? 'bg-amber-400 text-zinc-900 font-bold' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                                                data-testid="modal-talla-total"
                                            >
                                                Total: {totalModeloColor}
                                            </button>
                                            {todasMismoModelo
                                                .sort(sortTallas)
                                                .map((t, i) => (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => setInfoInv({ ...infoInv, talla: t.talla, foto_url: t.foto_url || infoInv.foto_url })}
                                                        className={`text-xs px-2 py-1 rounded transition ${t.talla === infoInv.talla ? 'bg-amber-400 text-zinc-900 font-bold' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                                                        data-testid={`modal-talla-${t.talla}`}
                                                    >
                                                        T{t.talla}: {t.stock}
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {/* Cortes vinculados / Última entrada/salida / Lotes */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700 text-xs space-y-1">
                                        {ultimaEntrada && (
                                            <p className="text-zinc-400">
                                                Ultima entrada: <span className="text-emerald-400">{ultimaEntrada.fecha}</span> · <span className="text-zinc-200">{ultimaEntrada.cantidad} pzas</span>
                                            </p>
                                        )}
                                        {ultimaSalida && (
                                            <p className="text-zinc-400">
                                                Ultima salida: <span className="text-red-400">{ultimaSalida.fecha}</span> · <span className="text-zinc-200">{ultimaSalida.cantidad} pzas</span>
                                            </p>
                                        )}
                                        {!ultimaEntrada && !ultimaSalida && <p className="text-zinc-500 italic">Sin movimientos</p>}
                                    </div>
                                    <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700 text-xs space-y-1">
                                        {cortesVinculados.length > 0 && (
                                            <div>
                                                <p className="text-zinc-500 mb-1">Cortes vinculados:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {cortesVinculados.slice(0, 4).map(c => (
                                                        <span key={c.id} className="badge-info text-[10px]">{c.modelo} · {c.color_tela}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {lotesRefs.length > 0 && (
                                            <p className="text-zinc-500 mt-1">
                                                Lotes/Refs: <span className="text-zinc-300">{lotesRefs.slice(0, 3).join(', ')}</span>
                                            </p>
                                        )}
                                        {cortesVinculados.length === 0 && lotesRefs.length === 0 && (
                                            <p className="text-zinc-500 italic">Sin cortes vinculados</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-semibold text-zinc-300 mb-2">Historial de movimientos</h3>
                                    <div className="overflow-x-auto rounded-lg border border-zinc-700 max-h-[300px] overflow-y-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-zinc-800">
                                                    <TableHead className="table-header">Fecha</TableHead>
                                                    <TableHead className="table-header">Tipo</TableHead>
                                                    <TableHead className="table-header">Cantidad</TableHead>
                                                    <TableHead className="table-header">Observaciones</TableHead>
                                                    {isAdmin && <TableHead className="table-header text-right">Acciones</TableHead>}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {movsProducto.length === 0 ? (
                                                    <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-zinc-500 py-6">Sin movimientos</TableCell></TableRow>
                                                ) : movsProducto.map(m => (
                                                    <TableRow key={m.id} className="table-row">
                                                        <TableCell className="table-cell">{m.fecha}</TableCell>
                                                        <TableCell className="table-cell">
                                                            {m.tipo === 'entrada'
                                                                ? <span className="badge-success">Entrada</span>
                                                                : <span className="badge-error">Salida</span>}
                                                        </TableCell>
                                                        <TableCell className={`table-cell font-bold ${m.tipo === 'entrada' ? 'text-emerald-400' : 'text-red-400'}`}>
                                                            {m.tipo === 'entrada' ? '+' : '-'}{m.cantidad}
                                                        </TableCell>
                                                        <TableCell className="table-cell text-xs text-zinc-400 max-w-xs truncate" title={m.observaciones}>{m.observaciones || '-'}</TableCell>
                                                        {isAdmin && (
                                                            <TableCell className="table-cell text-right">
                                                                <div className="inline-flex items-center gap-1">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => { setInfoInv(null); openEditModal(m); }}
                                                                        className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300"
                                                                        title="Editar movimiento"
                                                                        data-testid={`btn-detalle-editar-${m.id}`}
                                                                    >
                                                                        <Edit2 className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => { handleDeleteMov(m); }}
                                                                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                                                                        title="Eliminar movimiento"
                                                                        data-testid={`btn-detalle-eliminar-${m.id}`}
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Modal: Editar movimiento */}
            <Dialog open={!!editingMov} onOpenChange={(o) => { if (!o) setEditingMov(null); }}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="modal-editar-pt">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit2 className="w-5 h-5 text-blue-400" /> Editar movimiento
                        </DialogTitle>
                    </DialogHeader>
                    {editingMov && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="form-group"><Label className="form-label">Modelo</Label>
                                    <Input value={editForm.modelo || ''} onChange={e => setEditForm({ ...editForm, modelo: e.target.value })} data-testid="edit-modelo" />
                                </div>
                                <div className="form-group"><Label className="form-label">Talla</Label>
                                    <Input value={editForm.talla || ''} onChange={e => setEditForm({ ...editForm, talla: e.target.value })} data-testid="edit-talla" />
                                </div>
                                <div className="form-group"><Label className="form-label">Color</Label>
                                    <Input value={editForm.color || ''} onChange={e => setEditForm({ ...editForm, color: e.target.value })} data-testid="edit-color" />
                                </div>
                            </div>
                            <div className="form-group"><Label className="form-label">Fecha</Label>
                                <Input type="date" value={editForm.fecha || ''} onChange={e => setEditForm({ ...editForm, fecha: e.target.value })} />
                            </div>
                            {editingMov.tipo === 'entrada' ? (
                                <>
                                    <Card className="card bg-zinc-800/30">
                                        <CardContent className="p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="form-label flex items-center gap-2 text-xs">
                                                    <Package className="w-3 h-3" /> Paquetes
                                                </Label>
                                                <Button type="button" size="sm" onClick={() => setEditForm(f => ({ ...f, paquetes: [...(f.paquetes || []), { piezas_por_paquete: 12, cantidad_paquetes: 1 }] }))} className="bg-amber-400 text-zinc-900 h-7 text-xs">
                                                    <Plus className="w-3 h-3 mr-1" /> Agregar
                                                </Button>
                                            </div>
                                            {(editForm.paquetes || []).length === 0 ? (
                                                <p className="text-xs text-zinc-500 italic">Sin paquetes</p>
                                            ) : (editForm.paquetes || []).map((p, idx) => (
                                                <div key={idx} className="flex items-end gap-2">
                                                    <div className="flex-1">
                                                        <Label className="text-[10px] text-zinc-400">Pzs/paq</Label>
                                                        <Input type="number" min="1" value={p.piezas_por_paquete} onChange={e => updateEditPaquete(idx, 'piezas_por_paquete', parseInt(e.target.value) || 0)} className="h-8 text-xs" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <Label className="text-[10px] text-zinc-400"># paquetes</Label>
                                                        <Input type="number" min="1" value={p.cantidad_paquetes} onChange={e => updateEditPaquete(idx, 'cantidad_paquetes', parseInt(e.target.value) || 0)} className="h-8 text-xs" />
                                                    </div>
                                                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditForm(f => ({ ...f, paquetes: f.paquetes.filter((_, i) => i !== idx) }))} className="h-8 text-red-400">
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </CardContent>
                                    </Card>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="form-group"><Label className="form-label">Restos</Label>
                                            <Input type="number" min="0" value={editForm.restos || 0} onChange={e => setEditForm({ ...editForm, restos: parseInt(e.target.value) || 0 })} />
                                        </div>
                                        <div className="form-group"><Label className="form-label">Defectos</Label>
                                            <Input type="number" min="0" value={editForm.defectos || 0} onChange={e => setEditForm({ ...editForm, defectos: parseInt(e.target.value) || 0 })} />
                                        </div>
                                        <div className="form-group"><Label className="form-label">Faltantes</Label>
                                            <Input type="number" min="0" value={editForm.faltantes || 0} onChange={e => setEditForm({ ...editForm, faltantes: parseInt(e.target.value) || 0 })} />
                                        </div>
                                    </div>
                                    <div className="text-xs text-zinc-400 text-right">
                                        Total recalculado: <span className="text-emerald-400 font-bold">{(editForm.paquetes || []).reduce((s, p) => s + (parseInt(p.piezas_por_paquete) || 0) * (parseInt(p.cantidad_paquetes) || 0), 0) + (parseInt(editForm.restos) || 0)} pzas</span>
                                    </div>
                                </>
                            ) : (
                                <div className="form-group"><Label className="form-label">Cantidad</Label>
                                    <Input type="number" min="1" value={editForm.cantidad || 0} onChange={e => setEditForm({ ...editForm, cantidad: parseInt(e.target.value) || 0 })} />
                                </div>
                            )}
                            <div className="form-group"><Label className="form-label">Observaciones</Label>
                                <Input value={editForm.observaciones || ''} onChange={e => setEditForm({ ...editForm, observaciones: e.target.value })} />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setEditingMov(null)}>Cancelar</Button>
                        <Button type="button" onClick={handleEditSubmit} className="bg-blue-500 hover:bg-blue-600" data-testid="btn-guardar-edit-pt">
                            Guardar cambios
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
